import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
  memo,
  type ChangeEvent as ReactChangeEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { createPortal } from 'react-dom'
import {
  CheckIcon,
  ArchiveBoxIcon,
  PlusIcon,
  MinusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  XMarkIcon,
  XCircleIcon,
  InformationCircleIcon,
  ClipboardDocumentIcon,
  Bars3Icon,
  TagIcon,
  Squares2X2Icon,
  ArrowsRightLeftIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline'
import { CheckIcon as CheckIconSolid } from '@heroicons/react/24/solid'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  getDocument,
  updateDocument,
  refreshDocumentCategories,
  allocateSharedCosts,
} from '@/services/documents'
import {
  getQboListsShared,
  createVendor,
  getPaymentTerms,
  getTermDueDatePreview,
  type QBOCategory,
  type QBOLocation,
  type QBOTerm,
  type QBOVendor,
} from '@/services/qbo'
import type { Document, LineItem, LineItemCreate } from '@/types/document'
import { findMatchingQboVendorDisplayName } from '@/utils/qboVendorMatch'
import { qboVendorDetailUrl, resolveQboVendorId } from '@/utils/qboVendorUrl'
import SearchableSelect, { type SelectOption } from './SearchableSelect'
import DateInput from './DateInput'
import { formatCalendarDate, formatLocalDateTime } from '@/utils/dateFormat'
import PdfViewer from './PdfViewer'
import CsvPreviewTable from './CsvPreviewTable'
import EmailRuleEditModal from './EmailRuleEditModal'
import { getEmailRule } from '@/services/emailRules'
import type { EmailRule } from '@/types/emailRule'
import { getSetting } from '@/services/settings'
import {
  classifyImLineFlags,
  classifySharedKindFromDescription,
  DEFAULT_SHARED_COST_KEYWORDS,
  parseSharedCostKeywords,
  SHARED_COST_KEYWORDS_KEY,
} from '@/utils/sharedCostKeywords'
import { useMaxMd } from '@/hooks/useMaxMd'
import {
  isCsvUploadDocument,
  stripCsvExtractionPrompt,
} from '@/utils/csvPreview'
import {
  enrichPurchasingDocument,
  listPurchasingPurchaseLines,
  pollEnrichmentJob,
} from '@/services/purchasing'
import type { PurchasingPurchaseLineRow } from '@/services/purchasingTypes'
import type { BackgroundJob } from '@/services/jobs'
import { toast } from '@/store/toastStore'
import { formatApiError, getApiErrorDetail } from '@/utils/apiError'
import {
  getAmberReviewFlagsFromNeedsReviewFlags,
  isBlockingReviewFlag,
} from '@/utils/docReviewAmberFlags'
import { getReviewFlagLabel } from '@/utils/docReviewFilterPills'
import AmazonChargePreview from './AmazonChargePreview'
import { qboTxnUrl } from '@/utils/qboLinks'
import { docTypeLabel, docTypeRequiresLineItems, isCardDocType } from '@/utils/docTypeLabel'
import {
  amazonQboDuplicateMessage,
  amazonReportLocksTypeAndVendor,
  hasAmazonPreviewPayload,
  lineItemHeaderGridClass,
  pickDefaultDocumentPreviewMode,
  resolveApproveButtonLabel,
  type DocumentPreviewMode,
} from '@/utils/documentPanelAmazon'
import {
  documentMayHavePurchaseLines,
  EXTRACTION_MODEL_STRONG,
  formatPurchaseDecimal,
  mapPurchaseLinesToLineItems,
  purchaseLineProductBadge,
  STRONG_ENRICHMENT_ESTIMATED_COST,
  toLineItemHighlightData,
  type HighlightData,
} from '@/utils/documentPanelPurchasing'

interface Props {
  document: Document
  /** Parent is fetching full document (list rows omit email body, OCR, etc.). */
  previewHydrating?: boolean
  onClose: () => void
  onUpdate: (doc: Document) => void
  onApprove: (options?: { overrideNonPayable?: boolean; overrideQboDuplicate?: boolean }) => void
  onDelete: () => void
  onUnapprove?: () => void
  onRestore?: () => void
  onNavigatePrev?: () => void
  onNavigateNext?: () => void
  hasPrev?: boolean
  hasNext?: boolean
  isPendingDuplicate?: boolean
  /** Same vendor+ref as an already-approved doc in Bookkeeper (blocks Approve). */
  duplicateMatchesApproved?: boolean
  duplicateApprovedDocumentId?: number | null
  duplicateApprovedQboEntityId?: string | null
  duplicateApprovedQboEntityType?: string | null
  duplicateApprovedLineItemCount?: number | null
  duplicateApprovedTotalAmount?: string | null
  onOverwriteApprovedDuplicate?: () => void
  isOverwriting?: boolean
  overwriteError?: string | null
  onClearOverwriteError?: () => void
  isApproving?: boolean
  approvalError?: string | null
  onClearApprovalError?: () => void
  /** When false, user cannot approve or archive (page permission). */
  canEdit?: boolean
  /** Called after a new QBO vendor is created so the list view can refresh vendor flags. */
  onVendorCreated?: (vendor: QBOVendor) => void
}

const MIN_DETAILS_WIDTH = 350
const MIN_PDF_WIDTH = 350
const DEFAULT_DETAILS_PERCENT = 0.45
const PREVIEW_SPLIT_OPEN_STORAGE_KEY = 'bookkeeper-document-panel-preview-split-open'
const DOCUMENT_METADATA_COLLAPSED_STORAGE_KEY =
  'bookkeeper-document-panel-metadata-collapsed'

function readPreviewSplitOpenFromStorage(): boolean {
  try {
    const raw = window.localStorage.getItem(PREVIEW_SPLIT_OPEN_STORAGE_KEY)
    if (raw === '0' || raw === 'false') return false
    return true
  } catch {
    return true
  }
}

function readMetadataCollapsedFromStorage(): boolean {
  try {
    const raw = window.localStorage.getItem(DOCUMENT_METADATA_COLLAPSED_STORAGE_KEY)
    return raw === '1' || raw === 'true'
  } catch {
    return false
  }
}

function attachmentDownloadFilename(doc: Document): string {
  const vendor = doc.vendor_name || 'document'
  const ref = doc.reference_number || String(doc.id)
  const name = `${vendor}_${ref}.pdf`
  const sanitized = name.replace(/[^a-zA-Z0-9._ -]/g, '').trim()
  return sanitized || `document_${doc.id}.pdf`
}

const AUTOSAVE_DEBOUNCE_MS = 350
/** Show a non-blocking fixed-asset reminder in Document Details when any bill line meets this amount. */
const FIXED_ASSET_LINE_AMOUNT_THRESHOLD = 2500

/** Matches SearchableSelect `error` styling so native inputs show the same invalid state as line-item categories. */
const METADATA_FIELD_ERROR_CLASS =
  '!border-red-500 ring-2 ring-red-500 hover:!border-red-600 focus:!outline-none focus:!shadow-none focus:!border-red-500 focus:!ring-red-500'

/** Safety cap for error-recovery retries (e.g. stale line-item ID). */
const SAVE_RETRY_LIMIT = 3

/** Document Details Terms select: None (empty option), Due on receipt, then remaining names A–Z. */
function sortPaymentTermsForDetailsSelect(terms: QBOTerm[]): QBOTerm[] {
  const dueOnReceipt: QBOTerm[] = []
  const others: QBOTerm[] = []
  for (const t of terms) {
    if (t.name.trim().toLowerCase() === 'due on receipt') {
      dueOnReceipt.push(t)
    } else {
      others.push(t)
    }
  }
  others.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
  return [...dueOnReceipt, ...others]
}

function isAllocatedLineDescription(desc: string | null | undefined): boolean {
  return (desc ?? '').trimStart().startsWith('Allocated ')
}

/** Mirrors ``services.shared_cost_allocation.SharedKind`` / ``infer_shared_line_kind`` (IM rows, no ``qbo_regex_kind``). */
type InferSharedKind =
  | 'tax'
  | 'shipping'
  | 'discount'
  | 'personal_shopper'
  | 'express'
  | 'tip'
  | 'fee'

/** Loaded from settings on panel mount; defaults mirror backend keyword list. */
let sharedCostKeywords: readonly string[] = DEFAULT_SHARED_COST_KEYWORDS

function inferSharedLineKind(li: {
  description?: string | null
  is_tax?: boolean
  is_shipping?: boolean
  is_discount?: boolean
  is_personal_shopper?: boolean
  is_tip?: boolean
  is_fee?: boolean
}): InferSharedKind | null {
  const fromFlags = classifyImLineFlags(li)
  if (fromFlags !== null) return fromFlags
  return classifySharedKindFromDescription(li.description, sharedCostKeywords)
}

/** True for aggregate shared-cost sources (matches server ``line_item_has_unallocated_shared_cost``). */
function isSharedCostSourceLineItem(li: {
  description?: string | null
  is_tax?: boolean
  is_shipping?: boolean
  is_discount?: boolean
  is_personal_shopper?: boolean
  is_tip?: boolean
  is_fee?: boolean
}): boolean {
  return inferSharedLineKind(li) !== null
}

/** Lines allocation weights can apply to (``_product_lines`` in ``shared_cost_allocation.py``). */
function isProductLineForSharedAllocation(li: {
  description?: string | null
  is_tax?: boolean
  is_shipping?: boolean
  is_discount?: boolean
  is_personal_shopper?: boolean
  is_tip?: boolean
  is_fee?: boolean
}): boolean {
  if (isAllocatedLineDescription(li.description)) return false
  return inferSharedLineKind(li) === null
}

type LineItemCategoryGate = {
  description?: string | null
  category_id?: string | null
  is_tax?: boolean
  is_shipping?: boolean
  is_discount?: boolean
  is_personal_shopper?: boolean
  is_tip?: boolean
  is_fee?: boolean
}

function lineItemHasCategory(li: LineItemCategoryGate): boolean {
  return li.category_id != null && String(li.category_id).trim() !== ''
}

/** Mirrors ``line_item_requires_qbo_expense_category`` on the API. */
export function lineItemRequiresCategory(
  _docType: string,
  li: LineItemCategoryGate,
  lineItems: LineItemCategoryGate[],
): boolean {
  if (!isSharedCostSourceLineItem(li)) return true
  const allocationCanRun =
    lineItems.some(isSharedCostSourceLineItem) &&
    lineItems.some(isProductLineForSharedAllocation)
  return !allocationCanRun
}

/** Product lines need a category until shared costs are allocated; shared-cost source lines are categorized via allocation. */
export function linesMissingRequiredCategoriesForSubmit(
  docType: string,
  lineItems: LineItemCategoryGate[],
): boolean {
  return lineItems.some(
    (li) => lineItemRequiresCategory(docType, li, lineItems) && !lineItemHasCategory(li),
  )
}

function formatEmailPreviewDateTime(iso: string | null | undefined): string {
  return formatLocalDateTime(iso, '')
}

interface LineItemWithId extends Omit<
  LineItemCreate,
  'id' | 'is_tax' | 'is_shipping' | 'is_discount' | 'is_personal_shopper' | 'is_tip' | 'is_fee'
> {
  /** Persisted / API line item id (``new-…`` until first save assigns a numeric id). */
  id: string
  /** Stable identity for React + DnD; unchanged when ``id`` is promoted from client to DB id. */
  rowKey: string
  is_tax: boolean
  is_shipping: boolean
  is_discount: boolean
  is_personal_shopper: boolean
  is_tip: boolean
  is_fee: boolean
}

type PanelFormData = {
  vendor_name: string
  doc_type: string
  issue_date: string
  due_date: string
  qbo_sales_term_id: string
  location: string
  location_id: string
  reference_number: string
  memo: string
}

function qboSalesTermIdFromExtracted(
  extracted: Record<string, unknown> | null | undefined,
): string {
  if (!extracted) return ''
  const v = extracted.qbo_sales_term_id
  if (v == null || v === '') return ''
  return String(v)
}

function newLineItemClientId(): string {
  return `new-${crypto.randomUUID()}`
}

function newLineItemRowKey(): string {
  return crypto.randomUUID()
}

function formatLineItemCurrency(amount: number): string {
  const rounded = Math.round(amount * 100) / 100
  if (rounded === 0) return '$0.00'
  if (rounded < 0) {
    return `($${Math.abs(rounded).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
  }
  return `$${rounded.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatViewInQboLabel(
  lineItemCount: number | null | undefined,
  totalAmount: string | number | null | undefined,
): string {
  const hasCount = lineItemCount != null
  const parsedTotal =
    totalAmount != null && totalAmount !== '' ? parseFloat(String(totalAmount)) : Number.NaN
  const hasTotal = !Number.isNaN(parsedTotal)
  if (!hasCount && !hasTotal) return 'View in QBO'

  const parts: string[] = []
  if (hasCount) {
    parts.push(`${lineItemCount} line item${lineItemCount === 1 ? '' : 's'}`)
  }
  if (hasTotal) {
    parts.push(`${formatLineItemCurrency(parsedTotal)} total`)
  }
  return `View in QBO (${parts.join(', ')})`
}

function formatLineItemAmount(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '0.00'
  return num.toFixed(2)
}

export function mapApiLineItemsToFormState(items: LineItem[]): LineItemWithId[] {
  return [...items]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((li) => ({
      id: String(li.id),
      rowKey: String(li.id),
      category_id: li.category_id,
      category_name: li.category_name,
      category_confidence: li.category_confidence,
      description: li.description,
      amount: parseFloat(String(li.amount)).toFixed(2),
      sort_order: li.sort_order,
      is_tax: Boolean(li.is_tax),
      is_shipping: Boolean(li.is_shipping),
      is_discount: Boolean(li.is_discount),
      is_personal_shopper: Boolean(li.is_personal_shopper),
      is_tip: Boolean(li.is_tip),
      is_fee: Boolean(li.is_fee),
      quantity: li.quantity,
      unit_price: li.unit_price,
      vendor_item_code: li.vendor_item_code,
    }))
}

/**
 * Reuse ``rowKey`` from ``prevRows`` when applying server ``mapped`` rows so list identity
 * stays stable when a client ``new-…`` id becomes a numeric DB id (prevents input remount / blur).
 */
function attachStableRowKeys(
  prevRows: LineItemWithId[],
  mappedRows: LineItemWithId[],
): LineItemWithId[] {
  const usedPrev = new Set<number>()
  return mappedRows.map((row) => {
    let prevIdx = prevRows.findIndex((p, pi) => !usedPrev.has(pi) && p.id === row.id)
    if (prevIdx === -1) {
      prevIdx = prevRows.findIndex(
        (p, pi) =>
          !usedPrev.has(pi) &&
          p.sort_order === row.sort_order &&
          !/^\d+$/.test(p.id) &&
          /^\d+$/.test(row.id),
      )
    }
    if (prevIdx !== -1) {
      usedPrev.add(prevIdx)
      return { ...row, rowKey: prevRows[prevIdx]!.rowKey }
    }
    return row
  })
}

/** Sum line amounts in whole cents to avoid binary-float drift in totals. */
function sumLineItemAmountRowsCents(rows: { amount: string | number }[]): number {
  return rows.reduce((sum, li) => {
    const n = parseFloat(String(li.amount))
    if (!Number.isFinite(n)) return sum
    return sum + Math.round(n * 100)
  }, 0)
}

function sumLineItemAmountRows(rows: { amount: string | number }[]): number {
  return sumLineItemAmountRowsCents(rows) / 100
}

function sumApiLineItemsTotal(items: LineItem[] | undefined): number {
  if (!items?.length) return 0
  return sumLineItemAmountRows(items)
}

export function buildLineItemUpdatePayload(items: LineItemWithId[]): LineItemCreate[] {
  return items.map((item, idx) => {
    const amountNum = parseFloat(String(item.amount))
    const row: LineItemCreate = {
      category_id: item.category_id ?? null,
      category_name: item.category_name ?? null,
      category_confidence: item.category_confidence ?? null,
      description: item.description ?? null,
      amount: Number.isNaN(amountNum) ? '0.00' : amountNum.toFixed(2),
      sort_order: item.sort_order ?? idx,
    }
    if (/^\d+$/.test(item.id)) {
      row.id = parseInt(item.id, 10)
    }
    row.is_tax = item.is_tax
    row.is_shipping = item.is_shipping
    row.is_discount = item.is_discount
    row.is_personal_shopper = item.is_personal_shopper
    row.is_tip = item.is_tip
    row.is_fee = item.is_fee
    row.quantity = item.quantity ?? null
    row.unit_price = item.unit_price ?? null
    row.vendor_item_code = item.vendor_item_code ?? null
    return row
  })
}

interface SortableLineItemProps {
  item: LineItemWithId
  index: number
  isReadOnly: boolean
  isSelected: boolean
  isMaxMd: boolean
  categoryRequired: boolean
  categoryOptions: SelectOption[]
  loadingQBO: boolean
  onCategoryChange: (index: number, id: string | null, label: string | null) => void
  onUpdateLineItem: <K extends keyof LineItemCreate>(
    index: number,
    field: K,
    value: LineItemCreate[K],
  ) => void
  onRemoveLineItem: (index: number) => void
  onAddLineItemAt: (index: number) => void
  onLineItemSelectionCheckboxMouseDown: (
    index: number,
    rowKey: string,
    e: ReactMouseEvent<HTMLInputElement>,
  ) => void
  onLineItemSelectionCheckboxChange: (
    index: number,
    rowKey: string,
    e: ReactChangeEvent<HTMLInputElement>,
  ) => void
  formatAmount: (value: string) => string
  formatCurrency: (value: number) => string
  onRowHover?: (data: HighlightData | null) => void
  onSaveFlushAfterFieldBlur?: () => void
  skuExpanderEligible?: boolean
  skuExpanded?: boolean
  purchaseLine?: PurchasingPurchaseLineRow | null
  purchaseLinesLoading?: boolean
  showSkuChevron?: boolean
  onToggleSkuExpand?: () => void
  showAmazonQtyUnit?: boolean
}

const SortableLineItem = memo(function SortableLineItem({
  item,
  index,
  isReadOnly,
  isSelected,
  isMaxMd,
  categoryRequired,
  categoryOptions,
  loadingQBO,
  onCategoryChange,
  onUpdateLineItem,
  onRemoveLineItem,
  onAddLineItemAt,
  onLineItemSelectionCheckboxMouseDown,
  onLineItemSelectionCheckboxChange,
  formatAmount,
  formatCurrency,
  onRowHover,
  onSaveFlushAfterFieldBlur,
  skuExpanderEligible = false,
  skuExpanded = false,
  purchaseLine = null,
  purchaseLinesLoading = false,
  showSkuChevron = false,
  onToggleSkuExpand,
  showAmazonQtyUnit = false,
}: SortableLineItemProps) {
  const [isEditing, setIsEditing] = useState(false)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.rowKey })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : undefined,
  }

  const handleRowMouseEnter = () => {
    if (!isEditing) {
      onRowHover?.(toLineItemHighlightData(item, 'row'))
    }
  }

  const handleRowMouseLeave = () => {
    if (!isEditing) {
      onRowHover?.(null)
    }
  }

  const cardClass = `rounded-lg border border-gray-200/80 p-3 transition-colors ${isDragging ? 'shadow-lg' : ''} ${isSelected ? 'bg-blue-50 ring-1 ring-blue-200' : 'bg-gray-50'}`

  const productBadge = purchaseLine ? purchaseLineProductBadge(purchaseLine) : null

  if (isMaxMd) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={cardClass}
        onMouseEnter={handleRowMouseEnter}
        onMouseLeave={handleRowMouseLeave}
      >
        <div className="min-w-0 w-full space-y-2">
          {!isReadOnly && (
            <label className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border border-transparent px-0.5 py-1 hover:bg-gray-100/80">
              <input
                type="checkbox"
                checked={isSelected}
                onMouseDown={(e) => onLineItemSelectionCheckboxMouseDown(index, item.rowKey, e)}
                onChange={(e) => onLineItemSelectionCheckboxChange(index, item.rowKey, e)}
                className="h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                aria-label="Select line item"
                title="Shift+click another checkbox to select a range"
              />
              <span className="text-xs font-medium text-gray-600">Select for bulk actions</span>
            </label>
          )}
          <div className="min-w-0 w-full">
            <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Category</span>
            {isReadOnly ? (
              <div className="mt-0.5 text-sm text-gray-700">{item.category_name || '-'}</div>
            ) : (
              <div className="mt-1 min-w-0 w-full">
                <SearchableSelect
                  options={categoryOptions}
                  value={item.category_id ?? null}
                  onChange={(id, label) => onCategoryChange(index, id, label)}
                  placeholder="Category..."
                  disabled={loadingQBO}
                  error={categoryRequired && !item.category_id}
                  warning={item.category_id != null && item.category_confidence != null && item.category_confidence < 1.0}
                  className="w-full min-w-0"
                />
              </div>
            )}
          </div>
          <div className="min-w-0 w-full">
            <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Description</span>
            {isReadOnly ? (
              <div className="mt-0.5 break-words text-sm text-gray-700">{item.description || '-'}</div>
            ) : (
              <input
                type="text"
                className="input-sm mt-1 w-full min-w-0"
                placeholder="Description"
                value={item.description || ''}
                onChange={(e) => onUpdateLineItem(index, 'description', e.target.value)}
                onFocus={() => setIsEditing(true)}
                onBlur={() => {
                  setIsEditing(false)
                  onSaveFlushAfterFieldBlur?.()
                }}
              />
            )}
          </div>
          {showAmazonQtyUnit ? (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Qty</span>
                <div className="mt-0.5 text-sm tabular-nums text-gray-700">{item.quantity ?? '—'}</div>
              </div>
              <div>
                <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Unit price</span>
                <div className="mt-0.5 text-sm tabular-nums text-gray-700">
                  {item.unit_price != null && Number.isFinite(Number(item.unit_price))
                    ? formatCurrency(Number(item.unit_price))
                    : '—'}
                </div>
              </div>
            </div>
          ) : null}
          <div className="flex min-w-0 w-full items-end gap-2">
            <div className="min-w-0 flex-1">
              <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Amount</span>
              {isReadOnly ? (
                <div className="mt-0.5 text-sm font-medium text-gray-900">
                  {formatCurrency(parseFloat(item.amount as string) || 0)}
                </div>
              ) : (
                <input
                  type="text"
                  inputMode="decimal"
                  className="input-sm mt-1 w-full min-w-0 text-right"
                  value={item.amount}
                  onChange={(e) => onUpdateLineItem(index, 'amount', e.target.value)}
                  onFocus={() => setIsEditing(true)}
                  onBlur={(e) => {
                    setIsEditing(false)
                    onUpdateLineItem(index, 'amount', formatAmount(e.target.value))
                    onSaveFlushAfterFieldBlur?.()
                  }}
                />
              )}
            </div>
            {!isReadOnly && (
              <div className="flex shrink-0 gap-1 pb-0.5">
                <button
                  type="button"
                  onClick={() => onRemoveLineItem(index)}
                  className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
                  title="Remove row"
                >
                  <MinusIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => onAddLineItemAt(index)}
                  className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-blue-500 transition-colors hover:bg-blue-50 hover:text-blue-700"
                  title="Add row below"
                >
                  <PlusIcon className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div ref={setNodeRef} style={style} className="space-y-0">
      <div
        className={`grid ${lineItemHeaderGridClass(isReadOnly, showAmazonQtyUnit)} gap-2 items-center p-2 rounded-lg border border-transparent hover:bg-gray-100 transition-colors ${isDragging ? 'shadow-lg' : ''} ${isSelected ? 'bg-blue-50 ring-1 ring-blue-200' : 'bg-gray-50'}`}
        onMouseEnter={handleRowMouseEnter}
        onMouseLeave={handleRowMouseLeave}
      >
        {!isReadOnly && (
          <input
            type="checkbox"
            checked={isSelected}
            onMouseDown={(e) => onLineItemSelectionCheckboxMouseDown(index, item.rowKey, e)}
            onChange={(e) => onLineItemSelectionCheckboxChange(index, item.rowKey, e)}
            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
            title="Shift+click another checkbox to select a range"
          />
        )}
        {!isReadOnly && (
          <div className="flex items-center justify-center gap-0.5">
            {showSkuChevron && (
              <button
                type="button"
                onClick={() => onToggleSkuExpand?.()}
                className="p-0.5 text-gray-400 hover:text-gray-700"
                title={skuExpanded ? 'Hide SKU details' : 'Show SKU details'}
                disabled={purchaseLinesLoading}
                aria-expanded={skuExpanded}
              >
                {purchaseLinesLoading ? (
                  <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                ) : skuExpanded ? (
                  <ChevronUpIcon className="w-3.5 h-3.5" />
                ) : (
                  <ChevronDownIcon className="w-3.5 h-3.5" />
                )}
              </button>
            )}
            <button
              type="button"
              className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-0.5"
              {...attributes}
              {...listeners}
            >
              <Bars3Icon className="w-4 h-4" />
            </button>
          </div>
        )}

      {isReadOnly ? (
        <div className="flex min-w-0 items-center gap-1 truncate text-sm text-gray-700">
          {showSkuChevron && (
            <button
              type="button"
              onClick={() => onToggleSkuExpand?.()}
              className="shrink-0 p-0.5 text-gray-400 hover:text-gray-700"
              title={skuExpanded ? 'Hide SKU details' : 'Show SKU details'}
              disabled={purchaseLinesLoading}
              aria-expanded={skuExpanded}
            >
              {purchaseLinesLoading ? (
                <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
              ) : skuExpanded ? (
                <ChevronUpIcon className="w-3.5 h-3.5" />
              ) : (
                <ChevronDownIcon className="w-3.5 h-3.5" />
              )}
            </button>
          )}
          <span className="truncate">{item.category_name || '-'}</span>
        </div>
      ) : (
          <SearchableSelect
            options={categoryOptions}
            value={item.category_id ?? null}
            onChange={(id, label) => onCategoryChange(index, id, label)}
            placeholder="Category..."
            disabled={loadingQBO}
            error={categoryRequired && !item.category_id}
            warning={item.category_id != null && item.category_confidence != null && item.category_confidence < 1.0}
          />
        )}

        {isReadOnly ? (
          <div className="text-sm text-gray-700 truncate">{item.description || '-'}</div>
        ) : (
          <input
            type="text"
            className="input-sm"
            placeholder="Description"
            value={item.description || ''}
            onChange={(e) => onUpdateLineItem(index, 'description', e.target.value)}
            onFocus={() => setIsEditing(true)}
            onBlur={() => {
              setIsEditing(false)
              onSaveFlushAfterFieldBlur?.()
            }}
          />
        )}

        {showAmazonQtyUnit ? (
          <>
            <div className="text-sm text-right tabular-nums text-gray-700">{item.quantity ?? '—'}</div>
            <div className="text-sm text-right tabular-nums text-gray-700">
              {item.unit_price != null && Number.isFinite(Number(item.unit_price))
                ? formatCurrency(Number(item.unit_price))
                : '—'}
            </div>
          </>
        ) : null}

        {isReadOnly ? (
          <div className="text-sm text-gray-700 text-right">
            {formatCurrency(parseFloat(item.amount as string) || 0)}
          </div>
        ) : (
          <input
            type="text"
            inputMode="decimal"
            className="input-sm text-right"
            value={item.amount}
            onChange={(e) => onUpdateLineItem(index, 'amount', e.target.value)}
            onFocus={() => setIsEditing(true)}
            onBlur={(e) => {
              setIsEditing(false)
              onUpdateLineItem(index, 'amount', formatAmount(e.target.value))
              onSaveFlushAfterFieldBlur?.()
            }}
          />
        )}

        {!isReadOnly && (
          <div className="flex gap-1 justify-center flex-shrink-0">
            <button
              type="button"
              onClick={() => onRemoveLineItem(index)}
              className="p-1.5 rounded text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors"
              title="Remove row"
            >
              <MinusIcon className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onAddLineItemAt(index)}
              className="p-1.5 rounded text-blue-500 hover:text-blue-700 hover:bg-blue-50 transition-colors"
              title="Add row below"
            >
              <PlusIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {skuExpanded && purchaseLine && skuExpanderEligible && (
        <div className="mx-2 mb-1 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700">
          <div
            className={`hidden gap-2 md:grid ${
              isReadOnly
                ? 'grid-cols-[minmax(130px,1.2fr)_minmax(90px,1fr)_65px_56px]'
                : 'grid-cols-[20px_24px_minmax(130px,1.2fr)_minmax(90px,1fr)_65px_56px]'
            } md:items-end`}
          >
            {!isReadOnly ? <span aria-hidden="true" /> : null}
            {!isReadOnly ? <span aria-hidden="true" /> : null}
            <div>
              <span className="text-[10px] font-medium uppercase text-gray-500">Item code</span>
              {isReadOnly ? (
                <div className="mt-0.5 truncate">{item.vendor_item_code || purchaseLine.vendor_item_code || '—'}</div>
              ) : (
                <input
                  type="text"
                  className="input-sm mt-0.5 w-full min-w-0"
                  value={item.vendor_item_code ?? purchaseLine.vendor_item_code ?? ''}
                  onChange={(e) => onUpdateLineItem(index, 'vendor_item_code', e.target.value || null)}
                  onMouseEnter={() => onRowHover?.(toLineItemHighlightData(item, 'itemCode'))}
                  onMouseLeave={handleRowMouseLeave}
                  onFocus={() => setIsEditing(true)}
                  onBlur={() => {
                    setIsEditing(false)
                    onSaveFlushAfterFieldBlur?.()
                  }}
                />
              )}
            </div>
            <div>
              <span className="text-[10px] font-medium uppercase text-gray-500">Pack</span>
              <div className="mt-0.5 truncate" title={purchaseLine.pack_text ?? undefined}>
                {purchaseLine.pack_text || purchaseLine.pack_version_text || '—'}
              </div>
            </div>
            <div>
              <span className="text-[10px] font-medium uppercase text-gray-500">Qty</span>
              {isReadOnly ? (
                <div className="mt-0.5 tabular-nums text-right">{formatPurchaseDecimal(item.quantity ?? purchaseLine.quantity)}</div>
              ) : (
                <input
                  type="text"
                  inputMode="decimal"
                  className="input-sm mt-0.5 w-full min-w-0 text-right"
                  value={item.quantity ?? purchaseLine.quantity ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value.trim()
                    onUpdateLineItem(index, 'quantity', raw === '' ? null : parseFloat(raw))
                  }}
                  onFocus={() => setIsEditing(true)}
                  onBlur={() => {
                    setIsEditing(false)
                    onSaveFlushAfterFieldBlur?.()
                  }}
                />
              )}
            </div>
            <div>
              <span className="text-[10px] font-medium uppercase text-gray-500">Unit price</span>
              {isReadOnly ? (
                <div className="mt-0.5 tabular-nums text-right">
                  {formatPurchaseDecimal(item.unit_price ?? purchaseLine.unit_price)}
                </div>
              ) : (
                <input
                  type="text"
                  inputMode="decimal"
                  className="input-sm mt-0.5 w-full min-w-0 text-right"
                  value={item.unit_price ?? purchaseLine.unit_price ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value.trim()
                    onUpdateLineItem(index, 'unit_price', raw === '' ? null : parseFloat(raw))
                  }}
                  onFocus={() => setIsEditing(true)}
                  onBlur={() => {
                    setIsEditing(false)
                    onSaveFlushAfterFieldBlur?.()
                  }}
                />
              )}
            </div>
          </div>
          <div
            className={`mt-2 hidden gap-2 md:grid ${
              isReadOnly
                ? 'grid-cols-[minmax(130px,1.2fr)_minmax(90px,1fr)_65px_56px]'
                : 'grid-cols-[20px_24px_minmax(130px,1.2fr)_minmax(90px,1fr)_65px_56px]'
            } md:items-end`}
          >
            {!isReadOnly ? <span aria-hidden="true" /> : null}
            {!isReadOnly ? <span aria-hidden="true" /> : null}
            <div>
              <span className="text-[10px] font-medium uppercase text-gray-500">$/atomic</span>
              <div className="mt-0.5 tabular-nums">
                {formatPurchaseDecimal(purchaseLine.cost_per_atomic_unit, 4)}
              </div>
            </div>
            <div className="md:col-span-2">
              <span className="text-[10px] font-medium uppercase text-gray-500">Product</span>
              <div className="mt-0.5">
                {productBadge ? (
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${productBadge.className}`}
                  >
                    {productBadge.label}
                  </span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </div>
            </div>
            <span aria-hidden="true" />
          </div>
          <div className="grid gap-2 md:hidden sm:grid-cols-[minmax(90px,1fr)_minmax(90px,1fr)_72px_88px_88px_auto] sm:items-end">
            <div>
              <span className="text-[10px] font-medium uppercase text-gray-500">Item code</span>
              {isReadOnly ? (
                <div className="mt-0.5 truncate">{item.vendor_item_code || purchaseLine.vendor_item_code || '—'}</div>
              ) : (
                <input
                  type="text"
                  className="input-sm mt-0.5 w-full min-w-0"
                  value={item.vendor_item_code ?? purchaseLine.vendor_item_code ?? ''}
                  onChange={(e) => onUpdateLineItem(index, 'vendor_item_code', e.target.value || null)}
                  onMouseEnter={() => onRowHover?.(toLineItemHighlightData(item, 'itemCode'))}
                  onMouseLeave={handleRowMouseLeave}
                  onFocus={() => setIsEditing(true)}
                  onBlur={() => {
                    setIsEditing(false)
                    onSaveFlushAfterFieldBlur?.()
                  }}
                />
              )}
            </div>
            <div>
              <span className="text-[10px] font-medium uppercase text-gray-500">Pack</span>
              <div className="mt-0.5 truncate" title={purchaseLine.pack_text ?? undefined}>
                {purchaseLine.pack_text || purchaseLine.pack_version_text || '—'}
              </div>
            </div>
            <div>
              <span className="text-[10px] font-medium uppercase text-gray-500">Qty</span>
              {isReadOnly ? (
                <div className="mt-0.5 tabular-nums">{formatPurchaseDecimal(item.quantity ?? purchaseLine.quantity)}</div>
              ) : (
                <input
                  type="text"
                  inputMode="decimal"
                  className="input-sm mt-0.5 w-full min-w-0 text-right"
                  value={item.quantity ?? purchaseLine.quantity ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value.trim()
                    onUpdateLineItem(index, 'quantity', raw === '' ? null : parseFloat(raw))
                  }}
                  onFocus={() => setIsEditing(true)}
                  onBlur={() => {
                    setIsEditing(false)
                    onSaveFlushAfterFieldBlur?.()
                  }}
                />
              )}
            </div>
            <div>
              <span className="text-[10px] font-medium uppercase text-gray-500">Unit price</span>
              {isReadOnly ? (
                <div className="mt-0.5 tabular-nums text-right">
                  {formatPurchaseDecimal(item.unit_price ?? purchaseLine.unit_price)}
                </div>
              ) : (
                <input
                  type="text"
                  inputMode="decimal"
                  className="input-sm mt-0.5 w-full min-w-0 text-right"
                  value={item.unit_price ?? purchaseLine.unit_price ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value.trim()
                    onUpdateLineItem(index, 'unit_price', raw === '' ? null : parseFloat(raw))
                  }}
                  onFocus={() => setIsEditing(true)}
                  onBlur={() => {
                    setIsEditing(false)
                    onSaveFlushAfterFieldBlur?.()
                  }}
                />
              )}
            </div>
            <div>
              <span className="text-[10px] font-medium uppercase text-gray-500">$/atomic</span>
              <div className="mt-0.5 tabular-nums text-right">
                {formatPurchaseDecimal(purchaseLine.cost_per_atomic_unit, 4)}
              </div>
            </div>
            <div className="sm:text-right">
              <span className="text-[10px] font-medium uppercase text-gray-500">Product</span>
              <div className="mt-0.5">
                {productBadge ? (
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${productBadge.className}`}
                  >
                    {productBadge.label}
                  </span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
})

export default function DocumentPanel({
  document,
  previewHydrating = false,
  onClose,
  onUpdate,
  onApprove,
  onDelete,
  onUnapprove,
  onRestore,
  onNavigatePrev,
  onNavigateNext,
  hasPrev = false,
  hasNext = false,
  isPendingDuplicate = false,
  duplicateMatchesApproved = false,
  duplicateApprovedDocumentId = null,
  duplicateApprovedQboEntityId = null,
  duplicateApprovedQboEntityType = null,
  duplicateApprovedLineItemCount = null,
  duplicateApprovedTotalAmount = null,
  onOverwriteApprovedDuplicate,
  isOverwriting = false,
  overwriteError = null,
  onClearOverwriteError,
  isApproving = false,
  approvalError = null,
  onClearApprovalError,
  onVendorCreated,
  canEdit = true,
}: Props) {
  const [formData, setFormData] = useState({
    vendor_name: document.vendor_name || '',
    doc_type: document.doc_type || '',
    issue_date: document.issue_date?.split('T')[0] || '',
    due_date: document.due_date?.split('T')[0] || '',
    qbo_sales_term_id: qboSalesTermIdFromExtracted(
      document.extracted_data as Record<string, unknown> | null | undefined,
    ),
    location: document.location || '',
    location_id: document.location_id || '',
    reference_number: document.reference_number || '',
    memo: document.memo || '',
  })
  const [lineItems, setLineItems] = useState<LineItemWithId[]>(() =>
    mapApiLineItemsToFormState(document.line_items)
  )
  /** document.id that `lineItems` was last aligned to; avoids total-mismatch flash when props update before local state. */
  const [lineItemsBoundDocId, setLineItemsBoundDocId] = useState(() => document.id)

  const maxMd = useMaxMd()
  const [mobileDocTab, setMobileDocTab] = useState<'details' | 'preview'>('details')
  const [sharedCostKeywordsRevision, setSharedCostKeywordsRevision] = useState(0)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const setting = await getSetting(SHARED_COST_KEYWORDS_KEY)
        if (!cancelled) {
          sharedCostKeywords = parseSharedCostKeywords(setting.value)
          setSharedCostKeywordsRevision((n) => n + 1)
        }
      } catch {
        if (!cancelled) {
          sharedCostKeywords = DEFAULT_SHARED_COST_KEYWORDS
          setSharedCostKeywordsRevision((n) => n + 1)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!maxMd) setMobileDocTab('details')
  }, [maxMd])

  useEffect(() => {
    setMobileDocTab('details')
  }, [document.id])

  useEffect(() => {
    setPurchaseLinesByLineId(new Map())
    setPurchaseLinesFetchAttempted(false)
    setPurchaseLinesLoading(false)
    setExpandedSkuLineKeys(new Set())
  }, [document.id])

  const dndSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: maxMd ? 999 : 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [categories, setCategories] = useState<QBOCategory[]>([])
  const [locations, setLocations] = useState<QBOLocation[]>([])
  const [vendors, setVendors] = useState<QBOVendor[]>([])
  const [creatingVendor, setCreatingVendor] = useState(false)
  const [paymentTerms, setPaymentTerms] = useState<QBOTerm[]>([])
  const [loadingQBO, setLoadingQBO] = useState(true)
  const showPreviewPanel = useMemo(() => {
    if (document.source_type === 'email') {
      return true
    }
    if (hasAmazonPreviewPayload(document.extracted_data)) {
      return true
    }
    return !!(document.pdf_storage_path || document.raw_ocr_text)
  }, [document.source_type, document.extracted_data, document.pdf_storage_path, document.raw_ocr_text])

  const isCsvUpload = useMemo(() => isCsvUploadDocument(document), [document])

  const ocrPreviewText = useMemo(() => {
    if (!document.raw_ocr_text) return ''
    return isCsvUpload
      ? stripCsvExtractionPrompt(document.raw_ocr_text)
      : document.raw_ocr_text
  }, [document.raw_ocr_text, isCsvUpload])

  const [previewSplitOpen, setPreviewSplitOpen] = useState(readPreviewSplitOpenFromStorage)
  const showSplitPreview = showPreviewPanel && previewSplitOpen

  useEffect(() => {
    try {
      window.localStorage.setItem(PREVIEW_SPLIT_OPEN_STORAGE_KEY, previewSplitOpen ? '1' : '0')
    } catch {
      /* ignore quota / private mode */
    }
  }, [previewSplitOpen])

  // Wrap body in a subdocument so global rules in vendor HTML (e.g. body, *) cannot affect the app.
  const emailPreviewSrcDoc = useMemo(() => {
    const html = document.email_body_html
    if (!html) return ''
    return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/><base target="_blank" rel="noopener noreferrer"/></head><body style="margin:0;padding:12px;font-size:14px;line-height:1.5;font-family:ui-sans-serif,system-ui,-apple-system,sans-serif;color:#111827;">${html}</body></html>`
  }, [document.email_body_html])

  const emailHeaderDateTime = useMemo(
    () =>
      formatEmailPreviewDateTime(
        document.email_received_at ?? document.created_at,
      ),
    [document.email_received_at, document.created_at],
  )

  const hasEmailPreviewTab = document.source_type === 'email'

  const [previewMode, setPreviewMode] = useState<DocumentPreviewMode>('pdf')
  const [showRefreshModal, setShowRefreshModal] = useState(false)
  const [refreshingCategories, setRefreshingCategories] = useState(false)
  const [purchaseLinesByLineId, setPurchaseLinesByLineId] = useState<
    Map<string, PurchasingPurchaseLineRow>
  >(() => new Map())
  const [purchaseLinesLoading, setPurchaseLinesLoading] = useState(false)
  const [purchaseLinesFetchAttempted, setPurchaseLinesFetchAttempted] = useState(false)
  const [expandedSkuLineKeys, setExpandedSkuLineKeys] = useState<Set<string>>(() => new Set())
  const [enrichingSku, setEnrichingSku] = useState(false)
  const [showStrongEnrichModal, setShowStrongEnrichModal] = useState(false)
  const [allocatingSharedCosts, setAllocatingSharedCosts] = useState(false)
  const [showApproveConfirm, setShowApproveConfirm] = useState(false)
  const [showEmailRuleEdit, setShowEmailRuleEdit] = useState(false)
  const [linkedEmailRule, setLinkedEmailRule] = useState<EmailRule | null>(null)
  const [linkedEmailRuleLoading, setLinkedEmailRuleLoading] = useState(false)
  const [detailsWidthPercent, setDetailsWidthPercent] = useState(DEFAULT_DETAILS_PERCENT)
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    if (!previewSplitOpen) setIsDragging(false)
  }, [previewSplitOpen])

  useEffect(() => {
    setShowEmailRuleEdit(false)
    const ruleId = document.email_rule_id
    if (ruleId == null) {
      setLinkedEmailRule(null)
      setLinkedEmailRuleLoading(false)
      return
    }
    let cancelled = false
    setLinkedEmailRuleLoading(true)
    getEmailRule(ruleId)
      .then((rule) => {
        if (!cancelled) setLinkedEmailRule(rule)
      })
      .catch((err) => {
        console.error('Failed to load linked email rule:', err)
        if (!cancelled) setLinkedEmailRule(null)
      })
      .finally(() => {
        if (!cancelled) setLinkedEmailRuleLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [document.id, document.email_rule_id])

  const handleEmailRuleSaved = useCallback(
    (rule: EmailRule) => {
      setLinkedEmailRule(rule)
      onUpdate({
        ...document,
        amount_alert_threshold: rule.amount_alert_threshold,
      })
    },
    [document, onUpdate],
  )

  const emailRuleEditTitle = useMemo(() => {
    if (document.email_rule_id == null) return ''
    if (linkedEmailRuleLoading) return 'Edit processing email rule…'
    if (linkedEmailRule) return `Edit email rule: ${linkedEmailRule.vendor_name}`
    return 'Edit processing email rule'
  }, [document.email_rule_id, linkedEmailRule, linkedEmailRuleLoading])

  const [ocrCopied, setOcrCopied] = useState(false)
  const [highlightData, setHighlightData] = useState<HighlightData | null>(null)
  const highlightDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [selectedLineRowKeys, setSelectedLineRowKeys] = useState<Set<string>>(new Set())
  /** ``rowKey`` for the next SHIFT+click range; each range unions into ``selectedLineRowKeys``. */
  const lineItemSelectionAnchorKeyRef = useRef<string | null>(null)
  /**
   * After shift+range selection (mousedown preventDefault), React still delivers `change`
   * with checked=false for the endpoint row. Hold this ref until that event is ignored.
   */
  const suppressLineItemCheckboxChangeForKeyRef = useRef<string | null>(null)
  const [showCorrectionModal, setShowCorrectionModal] = useState(false)
  const [correctionInstructions, setCorrectionInstructions] = useState('')
  const [isCorrectingItems, setIsCorrectingItems] = useState(false)
  const [correctionError, setCorrectionError] = useState<string | null>(null)
  const [previousLineItems, setPreviousLineItems] = useState<LineItemWithId[] | null>(null)
  const [correctionModalPosition, setCorrectionModalPosition] = useState<{ x: number; y: number } | null>(null)
  const correctionModalDragRef = useRef<{ startX: number; startY: number; startPosX: number; startPosY: number } | null>(null)
  const [showBulkCategoryModal, setShowBulkCategoryModal] = useState(false)
  const [bulkCategoryId, setBulkCategoryId] = useState<string | null>(null)
  const [bulkCategoryName, setBulkCategoryName] = useState<string | null>(null)
  const [bulkCategoryError, setBulkCategoryError] = useState<string | null>(null)
  const [vendorDetailsCollapsed, setVendorDetailsCollapsed] = useState(
    readMetadataCollapsedFromStorage,
  )

  useEffect(() => {
    try {
      window.localStorage.setItem(
        DOCUMENT_METADATA_COLLAPSED_STORAGE_KEY,
        vendorDetailsCollapsed ? '1' : '0',
      )
    } catch {
      /* ignore quota / private mode */
    }
  }, [vendorDetailsCollapsed])

  const panelRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Serialized save queue so concurrent saves cannot clobber each other. */
  const saveChainRef = useRef<Promise<void>>(Promise.resolve())
  const previousDocIdRef = useRef(document.id)
  const documentSwitchChainRef = useRef(Promise.resolve())
  /** Status for the document shown on the previous commit; used when navigating away to snapshot-save. */
  const lastStableDocStatusRef = useRef<Document['status']>(document.status)
  /** After QBO + doc switch settles, `editGenerationRef` at this value means "no user edits yet" (Strict Mode–safe). */
  const autosaveBaselineGenRef = useRef<number | null>(null)
  /** True while/after closing the panel — skips all panel setState in save paths (background persist + in-flight saves). */
  const suppressPanelStateRef = useRef(false)
  /** Bumps only when the user commits an edit (blur, selection, line ops); used for save scheduling and in-flight drift detection. */
  const editGenerationRef = useRef(0)
  const activeDocumentIdRef = useRef(document.id)
  const documentStatusRef = useRef(document.status)

  activeDocumentIdRef.current = document.id
  documentStatusRef.current = document.status

  /** Prior `previewHydrating` value; starts false so first mount with hydrating=true is visible. */
  const prevPreviewHydratingRef = useRef(false)

  /**
   * When Doc Review finishes list→full fetch (`previewHydrating` true→false), apply
   * `document.line_items` to local state. Do **not** gate on `editGenerationRef`: list→full
   * hydration must always replace rows; ``updateLineItem`` bumps generation while typing so
   * in-flight saves do not clobber local line edits with a stale PUT response.
   */
  useEffect(() => {
    const wasHydrating = prevPreviewHydratingRef.current
    if (!previewHydrating && wasHydrating && document.id === lineItemsBoundDocId) {
      const mapped = mapApiLineItemsToFormState(document.line_items)
      lineItemsRef.current = mapped
      lineItemTotalRef.current = sumLineItemAmountRows(mapped)
      setLineItems(mapped)
    }
    prevPreviewHydratingRef.current = previewHydrating
  }, [previewHydrating, document.id, document.line_items, lineItemsBoundDocId])

  const isEditGenerationDirty = useCallback(() => {
    const baseline = autosaveBaselineGenRef.current
    const current = editGenerationRef.current
    if (baseline === null) {
      return current !== 0
    }
    return current !== baseline
  }, [])

  const isStatusReadOnly = document.status === 'approved' || document.status === 'deleted'
  const isReadOnly = isStatusReadOnly || !canEdit
  const amazonReportLocked = amazonReportLocksTypeAndVendor(document.source_type)
  const typeVendorReadOnly = isReadOnly || amazonReportLocked
  const showAmazonLineQtyUnit = document.source_type === 'amazon_report'
  const amazonChargeIncomplete =
    document.extracted_data?.amazon_charge_incomplete === true ||
    (document.needs_review_flags?.includes('amazon_charge_incomplete') ?? false)
  const canApproveOrArchive = canEdit && document.status === 'pending_review'

  useEffect(() => {
    async function loadQBOData() {
      try {
        setLoadingQBO(true)
        let cats: QBOCategory[] = []
        let locs: QBOLocation[] = []
        let vends: QBOVendor[] = []
        let bundleOk = false
        try {
          const bundle = await getQboListsShared()
          cats = bundle.categories
          locs = bundle.locations
          vends = bundle.vendors
          bundleOk = true
        } catch {
          bundleOk = false
        }
        if (!bundleOk) {
          throw new Error('QBO load failed')
        }
        setCategories(cats.filter((c) => c.active))
        setLocations(locs.filter((l) => l.active))
        const activeVendors = vends.filter((v) => v.active)
        setVendors(activeVendors)

        try {
          const termsList = await getPaymentTerms(false)
          setPaymentTerms(sortPaymentTermsForDetailsSelect(termsList.filter((t) => t.active)))
        } catch {
          setPaymentTerms([])
        }

        // Auto-match vendor if current vendor_name doesn't exactly match a QBO vendor
        if (document.vendor_name) {
          const exactMatch = activeVendors.find(v => v.display_name === document.vendor_name)
          if (!exactMatch) {
            const matchedVendor = findMatchingQboVendorDisplayName(document.vendor_name, activeVendors)
            if (matchedVendor) {
              setFormData((prev) => ({ ...prev, vendor_name: matchedVendor }))
            }
          }
        }
      } catch (err) {
        console.error('Failed to load QBO data:', err)
      } finally {
        setLoadingQBO(false)
      }
    }
    loadQBOData()
  }, [])

  const lineItemTotal = useMemo(
    () => sumLineItemAmountRows(lineItems),
    [lineItems],
  )

  const lineTotalForExtractedCompare = useMemo(() => {
    if (document.id !== lineItemsBoundDocId) {
      return sumApiLineItemsTotal(document.line_items)
    }
    return lineItemTotal
  }, [document.id, document.line_items, lineItemsBoundDocId, lineItemTotal])

  /** Whole-cent difference; avoids float bugs (e.g. 434.76−434.75 < 0.01 in IEEE). */
  const extractedVsLineItemsCentsDiff = useMemo(() => {
    if (!document.extracted_total) return null
    const extractedTotal = parseFloat(document.extracted_total)
    if (!Number.isFinite(extractedTotal)) return null
    return Math.abs(
      Math.round(extractedTotal * 100) -
        Math.round(lineTotalForExtractedCompare * 100)
    )
  }, [document.extracted_total, lineTotalForExtractedCompare])

  const amountExceedsEmailRuleThreshold = useMemo(() => {
    if (formData.doc_type !== 'bill') return false
    const thRaw = document.amount_alert_threshold
    if (thRaw == null || String(thRaw).trim() === '') return false
    const limit = parseFloat(String(thRaw))
    if (!Number.isFinite(limit)) return false
    return lineItemTotal > limit
  }, [formData.doc_type, document.amount_alert_threshold, lineItemTotal])

  const formDataRef = useRef(formData)
  const lineItemsRef = useRef(lineItems)
  const lineItemTotalRef = useRef(lineItemTotal)
  const onUpdateRef = useRef(onUpdate)

  useEffect(() => {
    formDataRef.current = formData
  }, [formData])

  useEffect(() => {
    lineItemsRef.current = lineItems
    lineItemTotalRef.current = lineItemTotal
  }, [lineItems, lineItemTotal])

  useEffect(() => {
    onUpdateRef.current = onUpdate
  }, [onUpdate])

  /**
   * Merge server-assigned DB ids into the current local lineItems without
   * replacing fields the user may have edited during the PUT round-trip.
   * Returns true if any client ids were promoted to DB ids.
   */
  const mergeNewIdsFromResponse = useCallback(
    (sentItems: LineItemWithId[], responseItems: LineItem[]): boolean => {
      const sentClientIds = sentItems
        .map((li, idx) => ({ idx, id: li.id }))
        .filter(({ id }) => !/^\d+$/.test(id))
      if (sentClientIds.length === 0) return false

      const sentNumericIds = new Set(
        sentItems.filter((li) => /^\d+$/.test(li.id)).map((li) => parseInt(li.id, 10)),
      )
      const newServerItems = responseItems.filter(
        (li) => !sentNumericIds.has(li.id),
      )
      if (newServerItems.length === 0) return false

      let merged = false
      const currentItems = lineItemsRef.current
      for (let ci = 0; ci < sentClientIds.length && ci < newServerItems.length; ci++) {
        const clientId = sentClientIds[ci]!.id
        const serverItem = newServerItems[ci]!
        const currentIdx = currentItems.findIndex((li) => li.id === clientId)
        if (currentIdx !== -1 && currentItems[currentIdx]) {
          currentItems[currentIdx] = { ...currentItems[currentIdx]!, id: String(serverItem.id) }
          merged = true
        }
      }
      if (merged) {
        lineItemsRef.current = [...currentItems]
      }
      return merged
    },
    [],
  )

  const runDocumentSave = useCallback(
    async (
      targetDocId: number,
      docStatus: Document['status'],
      currentFormData: PanelFormData,
      currentLineItems: LineItemWithId[],
      currentTotal: number,
      singleAttempt: boolean,
      showSavingUi = true,
      ignoreEditGeneration = false,
      /** When set with singleAttempt (e.g. save-after-navigate-away), drift uses this snapshot instead of the live ref (ref may already be reset for the next document). */
      frozenEditGeneration?: number,
    ): Promise<void> => {
      if (docStatus === 'approved' || docStatus === 'deleted') {
        return
      }

      const panelOk = () => !suppressPanelStateRef.current

      let toggledSavingUi = false
      try {
        if (showSavingUi && panelOk()) {
          setSaving(true)
          toggledSavingUi = true
        }
        if (panelOk()) {
          setError(null)
        }

        let attempts = 0
        const maxAttempts = singleAttempt ? 1 : SAVE_RETRY_LIMIT

        while (attempts < maxAttempts) {
          attempts += 1

          const formSnap = singleAttempt ? currentFormData : formDataRef.current
          const lineSnap = singleAttempt ? currentLineItems : lineItemsRef.current
          const totalSnap = singleAttempt ? currentTotal : lineItemTotalRef.current

          const genAtStart =
            singleAttempt && frozenEditGeneration !== undefined
              ? frozenEditGeneration
              : editGenerationRef.current

          if (
            !singleAttempt &&
            !ignoreEditGeneration &&
            autosaveBaselineGenRef.current !== null &&
            genAtStart === autosaveBaselineGenRef.current
          ) {
            break
          }

          const sanitizedLineItems = lineSnap.map((item) => {
            const amount = parseFloat(String(item.amount))
            return {
              ...item,
              amount: isNaN(amount) ? '0.00' : amount.toFixed(2),
            }
          })

          try {
            const updated = await updateDocument(targetDocId, {
              vendor_name: formSnap.vendor_name || null,
              doc_type: (formSnap.doc_type as
                | 'bill'
                | 'vendor_credit'
                | 'card_expense'
                | 'card_credit') || null,
              issue_date: formSnap.issue_date || null,
              due_date: formSnap.due_date || null,
              location: formSnap.location || null,
              location_id: formSnap.location_id || null,
              reference_number: formSnap.reference_number || null,
              memo: formSnap.memo || null,
              total_amount: totalSnap.toFixed(2),
              line_items: buildLineItemUpdatePayload(sanitizedLineItems),
              qbo_sales_term_id: formSnap.qbo_sales_term_id
                ? formSnap.qbo_sales_term_id
                : null,
            })
            if (activeDocumentIdRef.current !== targetDocId) {
              onUpdateRef.current(updated)
              break
            }

            // ── Gen drift: user edited during the PUT ──
            // The PUT saved an older snapshot; user's latest edits are still in
            // lineItemsRef / formDataRef.  Do NOT apply the response (it would
            // overwrite those edits) and do NOT retry in this invocation.
            // Instead, merge any newly-assigned DB ids from the response into the
            // current local state, then let the normal autosave debounce schedule
            // the next save with the user's latest data.
            if (
              !ignoreEditGeneration &&
              editGenerationRef.current !== genAtStart
            ) {
              mergeNewIdsFromResponse(sanitizedLineItems, updated.line_items)

              // Ensure a follow-up save is scheduled via the normal debounce path.
              // If there's no pending timer (the earlier debounce already fired to
              // start this save), schedule one now.
              if (!singleAttempt && !saveTimeoutRef.current && panelOk()) {
                saveTimeoutRef.current = setTimeout(() => {
                  saveTimeoutRef.current = null
                  void performSave()
                }, AUTOSAVE_DEBOUNCE_MS)
              }

              // Still update the parent table (non-line-item fields like updated_at)
              onUpdateRef.current(updated)
              break
            }

            // ── Success: no gen drift ──
            // Apply the response so local state gets server-assigned DB ids.
            if (panelOk()) {
              const mapped = attachStableRowKeys(
                lineItemsRef.current,
                mapApiLineItemsToFormState(updated.line_items),
              )
              lineItemsRef.current = mapped
              lineItemTotalRef.current = sumLineItemAmountRows(mapped)
              setLineItems(mapped)
              setSelectedLineRowKeys((prev) => {
                const allowed = new Set(mapped.map((x) => x.rowKey))
                const next = new Set<string>()
                prev.forEach((id) => {
                  if (allowed.has(id)) next.add(id)
                })
                return next
              })
              setLastSaved(new Date())
            }
            autosaveBaselineGenRef.current = genAtStart
            onUpdateRef.current(updated)
            break
          } catch (err: unknown) {
            const detail = getApiErrorDetail(err)
            const lineItemNotFound =
              typeof detail === 'string' &&
              /Line item id \d+ not found on document \d+/.test(detail)

            if (lineItemNotFound) {
              try {
                const fresh = await getDocument(targetDocId)
                if (activeDocumentIdRef.current !== targetDocId) {
                  onUpdateRef.current(fresh)
                  break
                }
                const mapped = attachStableRowKeys(
                  lineItemsRef.current,
                  mapApiLineItemsToFormState(fresh.line_items),
                )
                if (panelOk()) {
                  lineItemsRef.current = mapped
                  lineItemTotalRef.current = sumLineItemAmountRows(mapped)
                  setLineItems(mapped)
                  setSelectedLineRowKeys(new Set())
                  lineItemSelectionAnchorKeyRef.current = null
                  setError(null)
                }
                onUpdateRef.current(fresh)
                if (singleAttempt) break
                continue
              } catch (refreshErr) {
                console.error(refreshErr)
              }
            }

            if (panelOk()) {
              setError(formatApiError(err, 'Failed to save'))
            } else {
              console.error('Document save failed after panel closed:', detail || err)
            }
            console.error(err)
            break
          }
        }
      } finally {
        if (toggledSavingUi) {
          setSaving(false)
        }
      }
    },
    [mergeNewIdsFromResponse],
  )

  const performSave = useCallback((): Promise<void> => {
    const run = async (): Promise<void> => {
      await runDocumentSave(
        activeDocumentIdRef.current,
        documentStatusRef.current,
        formDataRef.current,
        lineItemsRef.current,
        lineItemTotalRef.current,
        false,
        true,
        false,
      )
    }

    const chained = saveChainRef.current.then(run, run)
    saveChainRef.current = chained.catch(() => {
      /* Prevent a rejected save from breaking the queue for subsequent flushes. */
    })
    return chained
  }, [runDocumentSave])

  const flushPendingSave = useCallback(async () => {
    const hadDebouncedSavePending = saveTimeoutRef.current !== null
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
    }
    await saveChainRef.current.catch(() => {})
    const generationDrifted = isEditGenerationDirty()
    if (hadDebouncedSavePending || generationDrifted) {
      await performSave()
    }
  }, [performSave, isEditGenerationDirty])

  const schedulePersistAfterCommit = useCallback(() => {
    editGenerationRef.current += 1
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null
      void performSave()
    }, AUTOSAVE_DEBOUNCE_MS)
  }, [performSave])

  /** After a committed field edit: bump generation, then flush once React/refs reflect the latest state. */
  const flushPersistAfterFieldCommit = useCallback(() => {
    editGenerationRef.current += 1
    window.setTimeout(() => {
      void flushPendingSave()
    }, 0)
  }, [flushPendingSave])

  useLayoutEffect(() => {
    if (document.id === previousDocIdRef.current) return

    const leavingId = previousDocIdRef.current
    const leavingStatus = lastStableDocStatusRef.current
    const leavingEditGenerationSnapshot = editGenerationRef.current
    previousDocIdRef.current = document.id

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
    }

    const nextDoc = document
    const snapForm: PanelFormData = { ...formDataRef.current }
    const snapLines = lineItemsRef.current.map((li) => ({ ...li }))
    const snapTotal = lineItemTotalRef.current

    /** New document session — avoids hydrate / refetch effects matching the previous doc's generation. */
    editGenerationRef.current = 0
    autosaveBaselineGenRef.current = 0

    const nextForm: PanelFormData = {
      vendor_name: nextDoc.vendor_name || '',
      doc_type: nextDoc.doc_type || '',
      issue_date: nextDoc.issue_date?.split('T')[0] || '',
      due_date: nextDoc.due_date?.split('T')[0] || '',
      qbo_sales_term_id: qboSalesTermIdFromExtracted(
        nextDoc.extracted_data as Record<string, unknown> | null | undefined,
      ),
      location: nextDoc.location || '',
      location_id: nextDoc.location_id || '',
      reference_number: nextDoc.reference_number || '',
      memo: nextDoc.memo || '',
    }
    const nextLines = mapApiLineItemsToFormState(nextDoc.line_items)
    const nextTotal = sumLineItemAmountRows(nextLines)

    formDataRef.current = nextForm
    lineItemsRef.current = nextLines
    lineItemTotalRef.current = nextTotal
    activeDocumentIdRef.current = nextDoc.id

    setFormData(nextForm)
    setLineItems(nextLines)
    setLineItemsBoundDocId(nextDoc.id)
    setSelectedLineRowKeys(new Set())
    lineItemSelectionAnchorKeyRef.current = null

    documentSwitchChainRef.current = documentSwitchChainRef.current
      .catch(() => {})
      .then(async () => {
        await saveChainRef.current.catch(() => {})
        const enqueueLeavingSave = (): Promise<void> =>
          runDocumentSave(
            leavingId,
            leavingStatus,
            snapForm,
            snapLines,
            snapTotal,
            true,
            false,
            false,
            leavingEditGenerationSnapshot,
          )
        const p = saveChainRef.current.then(enqueueLeavingSave, enqueueLeavingSave)
        saveChainRef.current = p.catch(() => {
          /* Prevent a rejected save from breaking the queue for subsequent flushes. */
        })
        await p
      })
  }, [document, runDocumentSave])

  useLayoutEffect(() => {
    lastStableDocStatusRef.current = document.status
  }, [document.id, document.status])

  const handleClose = useCallback(() => {
    const executeClose = () => {
      const hadDebouncedSavePending = saveTimeoutRef.current !== null
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = null
      }

      const generationDrifted = isEditGenerationDirty()
      const needsPersist =
        hadDebouncedSavePending ||
        generationDrifted

      suppressPanelStateRef.current = true

      if (
        needsPersist &&
        documentStatusRef.current === 'pending_review'
      ) {
        const docId = activeDocumentIdRef.current
        const status = documentStatusRef.current
        const form = { ...formDataRef.current }
        const lines = lineItemsRef.current.map((li) => ({ ...li }))
        const total = lineItemTotalRef.current

        const p = saveChainRef.current
          .catch(() => {})
          .then(() =>
            runDocumentSave(
              docId,
              status,
              form,
              lines,
              total,
              true,
              false,
              true,
            ),
          )
        saveChainRef.current = p.catch(() => {
          /* Prevent a rejected save from breaking the queue for subsequent flushes. */
        })
        void p
      }

      onClose()
    }

    const ae = globalThis.document?.activeElement ?? null
    if (
      ae instanceof HTMLElement &&
      panelRef.current?.contains(ae)
    ) {
      ae.blur()
      window.setTimeout(executeClose, 0)
      return
    }
    executeClose()
  }, [onClose, runDocumentSave, isEditGenerationDirty])

  useEffect(() => {
    if (loadingQBO) {
      return
    }
    if (autosaveBaselineGenRef.current === null) {
      autosaveBaselineGenRef.current = editGenerationRef.current
    }
  }, [loadingQBO])

  const addLineItemAt = useCallback(
    (index: number) => {
      setLineItems((prev) => {
        const newItems = [...prev]
        newItems.splice(index + 1, 0, {
          id: newLineItemClientId(),
          rowKey: newLineItemRowKey(),
          category_id: null,
          category_name: null,
          category_confidence: null,
          description: '',
          amount: '0.00',
          sort_order: index + 1,
          is_tax: false,
          is_shipping: false,
          is_discount: false,
          is_personal_shopper: false,
          is_tip: false,
          is_fee: false,
        })
        return newItems.map((item, i) => ({ ...item, sort_order: i }))
      })
      queueMicrotask(() => {
        schedulePersistAfterCommit()
      })
    },
    [schedulePersistAfterCommit],
  )

  function addLineItem() {
    setLineItems((prev) => [
      ...prev,
      {
        id: newLineItemClientId(),
        rowKey: newLineItemRowKey(),
        category_id: null,
        category_name: null,
        category_confidence: null,
        description: '',
        amount: '0.00',
        sort_order: prev.length,
        is_tax: false,
        is_shipping: false,
        is_discount: false,
        is_personal_shopper: false,
        is_tip: false,
        is_fee: false,
      },
    ])
    queueMicrotask(() => {
      schedulePersistAfterCommit()
    })
  }

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (over && active.id !== over.id) {
        setLineItems((prev) => {
          const oldIndex = prev.findIndex((item) => item.rowKey === active.id)
          const newIndex = prev.findIndex((item) => item.rowKey === over.id)
          const reordered = arrayMove(prev, oldIndex, newIndex)
          return reordered.map((item, i) => ({ ...item, sort_order: i }))
        })
        queueMicrotask(() => {
          schedulePersistAfterCommit()
        })
      }
    },
    [schedulePersistAfterCommit],
  )

  const removeLineItem = useCallback((index: number) => {
    const current = lineItemsRef.current
    const itemToRemove = current[index]
    if (itemToRemove) {
      if (lineItemSelectionAnchorKeyRef.current === itemToRemove.rowKey) {
        lineItemSelectionAnchorKeyRef.current = null
      }
      setSelectedLineRowKeys((prev) => {
        const next = new Set(prev)
        next.delete(itemToRemove.rowKey)
        return next
      })
    }
    setLineItems((prev) => prev.filter((_, i) => i !== index))
    queueMicrotask(() => {
      schedulePersistAfterCommit()
    })
  }, [schedulePersistAfterCommit])

  const updateLineItem = useCallback(
    (index: number, field: keyof LineItemCreate, value: unknown) => {
      // Keep ``lineItemsRef`` in sync inside the state updater so ``runDocumentSave`` never
      // snapshots stale rows after ``editGenerationRef`` has advanced (refs otherwise lag
      // one commit behind typing, which could PUT ``0.00`` for a new row then apply it).
      setLineItems((prev) => {
        const next = prev.map((item, i) =>
          i === index ? { ...item, [field]: value } : item,
        )
        lineItemsRef.current = next
        lineItemTotalRef.current = sumLineItemAmountRows(next)
        return next
      })
      // Line-item typing must advance edit generation so drift detection can skip stale PUTs.
      editGenerationRef.current += 1
    },
    [],
  )

  const handleCategoryChange = useCallback(
    (index: number, categoryId: string | null, categoryName: string | null) => {
      setLineItems((prev) =>
        prev.map((item, i) =>
          i === index
            ? {
                ...item,
                category_id: categoryId,
                category_name: categoryName,
                category_confidence: null,
              }
            : item,
        ),
      )
      queueMicrotask(() => {
        schedulePersistAfterCommit()
      })
    },
    [schedulePersistAfterCommit],
  )

  function applyBulkCategoryToSelection() {
    if (!bulkCategoryId) {
      setBulkCategoryError('Please choose a category')
      return
    }
    setLineItems((prev) =>
      prev.map((item) =>
        selectedLineRowKeys.has(item.rowKey)
          ? {
              ...item,
              category_id: bulkCategoryId,
              category_name: bulkCategoryName,
              category_confidence: null,
            }
          : item
      )
    )
    setSelectedLineRowKeys(new Set())
    lineItemSelectionAnchorKeyRef.current = null
    setBulkCategoryId(null)
    setBulkCategoryName(null)
    setBulkCategoryError(null)
    setShowBulkCategoryModal(false)
    schedulePersistAfterCommit()
  }

  function armSuppressLineItemCheckboxChangeForKey(rowKey: string) {
    suppressLineItemCheckboxChangeForKeyRef.current = rowKey
  }

  const handleLineItemSelectionCheckboxMouseDown = useCallback(
    (index: number, rowKey: string, e: ReactMouseEvent<HTMLInputElement>) => {
      if (!e.shiftKey || lineItemSelectionAnchorKeyRef.current === null) {
        return
      }
      const rows = lineItemsRef.current
      const anchorIdx = rows.findIndex(
        (li) => li.rowKey === lineItemSelectionAnchorKeyRef.current,
      )
      if (anchorIdx === -1) {
        return
      }
      e.preventDefault()
      const lo = Math.min(anchorIdx, index)
      const hi = Math.max(anchorIdx, index)
      armSuppressLineItemCheckboxChangeForKey(rowKey)
      setSelectedLineRowKeys((prev) => {
        const next = new Set(prev)
        for (let i = lo; i <= hi; i++) {
          const row = rows[i]
          if (row) next.add(row.rowKey)
        }
        return next
      })
    },
    [],
  )

  const handleLineItemSelectionCheckboxChange = useCallback(
    (_index: number, rowKey: string, e: ReactChangeEvent<HTMLInputElement>) => {
      if (suppressLineItemCheckboxChangeForKeyRef.current === rowKey) {
        const suppressedKey = rowKey
        setTimeout(() => {
          if (suppressLineItemCheckboxChangeForKeyRef.current === suppressedKey) {
            suppressLineItemCheckboxChangeForKeyRef.current = null
          }
        }, 0)
        return
      }
      lineItemSelectionAnchorKeyRef.current = rowKey
      const checked = e.target.checked
      setSelectedLineRowKeys((prev) => {
        const next = new Set(prev)
        if (checked) {
          next.add(rowKey)
        } else {
          next.delete(rowKey)
        }
        return next
      })
    },
    [],
  )

  const lineItemsAllSelected =
    lineItems.length > 0 && lineItems.every((li) => selectedLineRowKeys.has(li.rowKey))
  const lineItemsSomeSelected =
    selectedLineRowKeys.size > 0 && !lineItemsAllSelected

  const selectAllLineItemsCheckboxRef = useRef<HTMLInputElement>(null)
  const selectAllLineItemsMobileCheckboxRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    for (const ref of [selectAllLineItemsCheckboxRef, selectAllLineItemsMobileCheckboxRef]) {
      const el = ref.current
      if (el) {
        el.indeterminate = lineItemsSomeSelected
      }
    }
  }, [lineItemsSomeSelected])

  function toggleSelectAllLineItems() {
    if (lineItemsAllSelected) {
      setSelectedLineRowKeys(new Set())
      lineItemSelectionAnchorKeyRef.current = null
    } else {
      setSelectedLineRowKeys(new Set(lineItems.map((li) => li.rowKey)))
      lineItemSelectionAnchorKeyRef.current = lineItems[0]?.rowKey ?? null
    }
  }

  function handleVendorChange(vendorName: string | null) {
    setFormData((prev) => ({ ...prev, vendor_name: vendorName || '' }))
    queueMicrotask(() => {
      schedulePersistAfterCommit()
    })
  }

  async function handleCreateVendor(name: string): Promise<SelectOption | null> {
    const trimmed = name.trim()
    if (!trimmed || creatingVendor) return null
    setCreatingVendor(true)
    toast.success(`Creating vendor “${trimmed}” in QuickBooks…`)
    try {
      const newVendor = await createVendor(trimmed)
      setVendors((prev) => [...prev, newVendor])
      onVendorCreated?.(newVendor)
      handleVendorChange(newVendor.display_name)
      return { id: newVendor.display_name, label: newVendor.display_name }
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to create vendor'))
      throw err
    } finally {
      setCreatingVendor(false)
    }
  }

  function handleLocationChange(locationId: string) {
    const loc = locations.find((l) => l.id === locationId)
    const next = {
      ...formDataRef.current,
      location_id: locationId,
      location: loc?.name || '',
    }
    // performSave reads formDataRef; sync before flush so autosave
    // is not scheduled before React's useEffect copies state into the ref.
    formDataRef.current = next
    setFormData(next)
    flushPersistAfterFieldCommit()
  }

  async function handleRefreshCategories() {
    if (!formData.vendor_name) {
      setError('Vendor name is required to refresh categories')
      return
    }

    try {
      setRefreshingCategories(true)
      setError(null)
      const updated = await refreshDocumentCategories(document.id)
      setLineItems(
        attachStableRowKeys(lineItemsRef.current, mapApiLineItemsToFormState(updated.line_items)),
      )
      onUpdate(updated)
      setShowRefreshModal(false)
    } catch (err: unknown) {
      setError(formatApiError(err, 'Failed to refresh categories'))
      console.error(err)
    } finally {
      setRefreshingCategories(false)
    }
  }

  async function handleAllocateSharedCosts() {
    if (linesMissingRequiredCategoriesForSubmit(formData.doc_type, lineItems)) {
      return
    }

    try {
      setAllocatingSharedCosts(true)
      setError(null)
      await flushPendingSave()
      if (
        linesMissingRequiredCategoriesForSubmit(
          formData.doc_type,
          lineItemsRef.current,
        )
      ) {
        setError(
          'Assign a QuickBooks category to every product line before allocating shared costs.',
        )
        return
      }
      const updated = await allocateSharedCosts(document.id)
      setLineItems(
        attachStableRowKeys(lineItemsRef.current, mapApiLineItemsToFormState(updated.line_items)),
      )
      onUpdate(updated)
    } catch (err: unknown) {
      setError(formatApiError(err, 'Failed to allocate shared costs'))
      console.error(err)
    } finally {
      setAllocatingSharedCosts(false)
    }
  }

  useEffect(() => {
    if (!showRefreshModal) return

    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape' && !refreshingCategories) {
        setShowRefreshModal(false)
      }
    }

    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [showRefreshModal, refreshingCategories])

  useEffect(() => {
    if (!showCorrectionModal) return

    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isCorrectingItems) {
        setShowCorrectionModal(false)
        setCorrectionModalPosition(null)
        setCorrectionInstructions('')
        setCorrectionError(null)
      }
    }

    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [showCorrectionModal, isCorrectingItems])

  useEffect(() => {
    if (!showBulkCategoryModal) return

    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setShowBulkCategoryModal(false)
        setBulkCategoryId(null)
        setBulkCategoryName(null)
        setBulkCategoryError(null)
      }
    }

    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [showBulkCategoryModal])

  async function handleCorrectLineItems() {
    if (!correctionInstructions.trim()) {
      setCorrectionError('Please describe the issue with the selected line items')
      return
    }

    const selectedIndices = lineItems
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => selectedLineRowKeys.has(item.rowKey))
      .map(({ index }) => index)

    if (selectedIndices.length === 0) {
      setCorrectionError('No line items selected')
      return
    }

    try {
      setIsCorrectingItems(true)
      setCorrectionError(null)

      const { correctLineItems: correctLineItemsApi } = await import('@/services/documents')
      const result = await correctLineItemsApi(document.id, {
        line_item_indices: selectedIndices,
        instructions: correctionInstructions,
      })

      const snapshotBeforeChange = [...lineItems]
      let hasChanges = false

      setLineItems((prev) => {
        const updated: LineItemWithId[] = [...prev]
        for (const corrected of result.corrected_items) {
          const existingItem = updated[corrected.index]
          if (corrected.index >= 0 && corrected.index < updated.length && existingItem) {
            const newDescription = corrected.description
            const newAmount = parseFloat(String(corrected.amount)).toFixed(2)
            if (existingItem.description !== newDescription || existingItem.amount !== newAmount) {
              hasChanges = true
            }
            updated[corrected.index] = {
              ...existingItem,
              description: newDescription,
              amount: newAmount,
            }
          }
        }
        return updated
      })

      if (hasChanges) {
        setPreviousLineItems(snapshotBeforeChange)
      }

      setSelectedLineRowKeys(new Set())
      lineItemSelectionAnchorKeyRef.current = null
      setCorrectionInstructions('')
      setShowCorrectionModal(false)
      setCorrectionModalPosition(null)
    } catch (err: unknown) {
      setCorrectionError(formatApiError(err, 'Failed to correct line items'))
      console.error(err)
    } finally {
      setIsCorrectingItems(false)
    }
  }

  function handleUndoCorrection() {
    if (previousLineItems) {
      setLineItems(previousLineItems)
      setPreviousLineItems(null)
      queueMicrotask(() => {
        schedulePersistAfterCommit()
      })
    }
  }

  function handleCorrectionModalDragStart(e: React.MouseEvent) {
    if (maxMd) return
    if ((e.target as HTMLElement).tagName === 'TEXTAREA' || 
        (e.target as HTMLElement).tagName === 'INPUT' ||
        (e.target as HTMLElement).tagName === 'BUTTON') {
      return
    }
    
    const rect = (e.currentTarget.closest('.correction-modal-container') as HTMLElement)?.getBoundingClientRect()
    if (!rect) return
    
    correctionModalDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: correctionModalPosition?.x ?? rect.left,
      startPosY: correctionModalPosition?.y ?? rect.top,
    }
    e.preventDefault()
    
    const handleMove = (moveEvent: MouseEvent) => {
      if (correctionModalDragRef.current) {
        const dx = moveEvent.clientX - correctionModalDragRef.current.startX
        const dy = moveEvent.clientY - correctionModalDragRef.current.startY
        setCorrectionModalPosition({
          x: correctionModalDragRef.current.startPosX + dx,
          y: correctionModalDragRef.current.startPosY + dy,
        })
      }
    }
    
    const handleUp = () => {
      correctionModalDragRef.current = null
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('mouseup', handleUp)
    }
    
    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', handleUp)
  }

  function highlightAmountMatches(text: string, amount: string): React.ReactNode {
    const num = parseFloat(amount)
    if (isNaN(num) || num === 0) return text

    const variants: string[] = []
    const absNum = Math.abs(num)
    
    variants.push(absNum.toFixed(2))
    if (absNum === Math.floor(absNum)) {
      variants.push(String(Math.floor(absNum)))
    }
    variants.push(absNum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
    if (absNum >= 1000) {
      variants.push(absNum.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }))
    }
    
    const withCurrency = variants.flatMap(v => [v, `$${v}`, `$ ${v}`])
    const allVariants = [...new Set(withCurrency)]
    
    const escapedVariants = allVariants.map(v => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    const inner = escapedVariants.join('|')
    const pattern = new RegExp(`(?<![\\d,])(${inner})(?![\\d,.])`, 'gi')
    
    const parts = text.split(pattern)
    if (parts.length === 1) return text
    
    return parts.map((part, i) => {
      if (pattern.test(part)) {
        pattern.lastIndex = 0
        return <mark key={i} className="bg-yellow-200 px-0.5 rounded-sm">{part}</mark>
      }
      return part
    })
  }

  const debouncedSetHighlightData = useCallback((data: HighlightData | null) => {
    if (highlightDebounceRef.current) {
      clearTimeout(highlightDebounceRef.current)
    }
    highlightDebounceRef.current = setTimeout(() => {
      setHighlightData(data)
    }, 50)
  }, [])

  const skuExpanderEligible = useMemo(
    () => documentMayHavePurchaseLines(document.status, document.needs_review_flags),
    [document.status, document.needs_review_flags],
  )

  const hasEnrichmentFailedFlag = useMemo(
    () => document.needs_review_flags?.includes('enrichment_failed') ?? false,
    [document.needs_review_flags],
  )

  const canRunSkuEnrichment = document.status === 'approved'

  const loadPurchaseLinesForDocument = useCallback(async (): Promise<
    Map<string, PurchasingPurchaseLineRow>
  > => {
    if (purchaseLinesFetchAttempted) {
      return purchaseLinesByLineId
    }
    setPurchaseLinesLoading(true)
    try {
      const response = await listPurchasingPurchaseLines({ document_id: document.id })
      const mapped = mapPurchaseLinesToLineItems(lineItemsRef.current, response.rows)
      setPurchaseLinesByLineId(mapped)
      setPurchaseLinesFetchAttempted(true)
      return mapped
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load SKU data'))
      setPurchaseLinesFetchAttempted(true)
      return new Map()
    } finally {
      setPurchaseLinesLoading(false)
    }
  }, [document.id, purchaseLinesByLineId, purchaseLinesFetchAttempted])

  const handleToggleSkuExpand = useCallback(
    async (rowKey: string, lineId: string) => {
      const mapped = await loadPurchaseLinesForDocument()
      if (!mapped.has(lineId)) {
        toast.warning('No SKU data for this line item')
        return
      }
      setExpandedSkuLineKeys((prev) => {
        const next = new Set(prev)
        if (next.has(rowKey)) {
          next.delete(rowKey)
        } else {
          next.add(rowKey)
        }
        return next
      })
    },
    [loadPurchaseLinesForDocument],
  )

  const waitForEnrichmentJob = useCallback(async (jobId: number): Promise<BackgroundJob> => {
    for (;;) {
      const job = await pollEnrichmentJob(jobId)
      if (job.status === 'pending' || job.status === 'running') {
        await new Promise((resolve) => setTimeout(resolve, 1500))
        continue
      }
      return job
    }
  }, [])

  const handleRerunSkuEnrichment = useCallback(async () => {
    if (!canRunSkuEnrichment || enrichingSku) return
    setEnrichingSku(true)
    try {
      const { job_id: jobId } = await enrichPurchasingDocument(document.id, { strong: false })
      toast.warning('SKU enrichment started…')
      const job = await waitForEnrichmentJob(jobId)
      if (job.status === 'failed') {
        toast.error(job.error_message || 'SKU enrichment failed')
        return
      }
      toast.success('SKU enrichment completed')
      const fresh = await getDocument(document.id)
      onUpdate(fresh)
      setPurchaseLinesByLineId(new Map())
      setPurchaseLinesFetchAttempted(false)
      setExpandedSkuLineKeys(new Set())
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to start SKU enrichment'))
    } finally {
      setEnrichingSku(false)
    }
  }, [canRunSkuEnrichment, document.id, enrichingSku, onUpdate, waitForEnrichmentJob])

  const handleStrongSkuEnrichment = useCallback(async () => {
    if (!canRunSkuEnrichment || enrichingSku) return
    setShowStrongEnrichModal(false)
    setEnrichingSku(true)
    try {
      const { job_id: jobId } = await enrichPurchasingDocument(document.id, { strong: true })
      toast.warning(`Strong-model extraction (${EXTRACTION_MODEL_STRONG}) started…`)
      const job = await waitForEnrichmentJob(jobId)
      if (job.status === 'failed') {
        toast.error(job.error_message || 'Strong-model extraction failed')
        return
      }
      toast.success('Strong-model extraction completed')
      const fresh = await getDocument(document.id)
      onUpdate(fresh)
      setPurchaseLinesByLineId(new Map())
      setPurchaseLinesFetchAttempted(false)
      setExpandedSkuLineKeys(new Set())
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to start strong-model extraction'))
    } finally {
      setEnrichingSku(false)
    }
  }, [canRunSkuEnrichment, document.id, enrichingSku, onUpdate, waitForEnrichmentJob])

  const isBillMissingDates =
    formData.doc_type === 'bill' && (!formData.issue_date || !formData.due_date)
  const isCardMissingChargeDate =
    isCardDocType(formData.doc_type) && !formData.issue_date

  /** Shared-cost rows the API would try to spread (matches persisted line items after save). */
  const hasSharedCostSourcesForAllocation = useMemo(
    () => lineItems.some((li) => inferSharedLineKind(li) !== null),
    [lineItems, sharedCostKeywordsRevision],
  )
  /** At least one non–shared-cost line; without this the API returns “No product lines to allocate against.” */
  const hasProductLinesForSharedAllocation = useMemo(
    () => lineItems.some(isProductLineForSharedAllocation),
    [lineItems, sharedCostKeywordsRevision],
  )
  const sharedCostAllocationActionable = useMemo(
    () => hasSharedCostSourcesForAllocation && hasProductLinesForSharedAllocation,
    [hasSharedCostSourcesForAllocation, hasProductLinesForSharedAllocation],
  )

  /** True when vendor is blank or (after QBO load) not an active QBO vendor — matches SearchableSelect “no row selected” UX. */
  const vendorPickInvalid = useMemo(() => {
    const t = (formData.vendor_name ?? '').trim()
    if (!t) return true
    if (loadingQBO) return false
    return !vendors.some((v) => v.display_name === t)
  }, [formData.vendor_name, loadingQBO, vendors])

  const qboVendorId = useMemo(
    () => resolveQboVendorId(formData.vendor_name, vendors, loadingQBO),
    [formData.vendor_name, vendors, loadingQBO],
  )

  const hasNonPayableNotice = document.needs_review_flags?.includes('non_payable_notice') ?? false

  const expectedQboEntityTypeForDoc =
    formData.doc_type === 'bill'
      ? 'Bill'
      : formData.doc_type === 'vendor_credit'
        ? 'VendorCredit'
        : isCardDocType(formData.doc_type)
          ? 'Purchase'
          : null

  const duplicateApprovedQboTypeMismatch =
    duplicateMatchesApproved &&
    !!duplicateApprovedQboEntityType &&
    !!expectedQboEntityTypeForDoc &&
    duplicateApprovedQboEntityType !== expectedQboEntityTypeForDoc

  const duplicateApprovedQboUrl =
    duplicateApprovedQboEntityId != null
      ? qboTxnUrl(duplicateApprovedQboEntityType, duplicateApprovedQboEntityId)
      : null

  const hasQboDuplicateFlag = document.needs_review_flags?.includes('qbo_duplicate') ?? false

  const documentReadyForQboExceptDuplicate =
    document.status === 'pending_review' &&
    !vendorPickInvalid &&
    formData.doc_type &&
    formData.location_id &&
    lineItems.length > 0 &&
    !linesMissingRequiredCategoriesForSubmit(formData.doc_type, lineItems) &&
    !isBillMissingDates &&
    !isCardMissingChargeDate &&
    !sharedCostAllocationActionable

  const documentReadyForQbo = documentReadyForQboExceptDuplicate && !hasQboDuplicateFlag

  const canApprove = documentReadyForQboExceptDuplicate && !duplicateMatchesApproved

  const canOverwriteApprovedDuplicate =
    duplicateMatchesApproved &&
    !!duplicateApprovedQboEntityId &&
    !duplicateApprovedQboTypeMismatch &&
    documentReadyForQbo

  const overwriteDisabledReason = useMemo(() => {
    if (!duplicateMatchesApproved || !onOverwriteApprovedDuplicate) return null
    if (!duplicateApprovedQboEntityId) {
      return 'No linked QuickBooks record to replace.'
    }
    if (duplicateApprovedQboTypeMismatch) {
      const pendingLabel =
        formData.doc_type === 'vendor_credit'
          ? 'vendor credit'
          : formData.doc_type === 'card_expense'
            ? 'card expense'
            : formData.doc_type === 'card_credit'
              ? 'card credit'
              : 'bill'
      const approvedLabel =
        duplicateApprovedQboEntityType === 'VendorCredit'
          ? 'vendor credit'
          : duplicateApprovedQboEntityType === 'Purchase'
            ? 'expense'
            : 'bill'
      return `Types differ: approved copy is a ${approvedLabel}, this document is a ${pendingLabel}.`
    }
    if (!documentReadyForQbo) {
      return 'Complete required fields and fix other blocking issues first.'
    }
    return null
  }, [
    duplicateMatchesApproved,
    onOverwriteApprovedDuplicate,
    duplicateApprovedQboEntityId,
    duplicateApprovedQboTypeMismatch,
    duplicateApprovedQboEntityType,
    formData.doc_type,
    documentReadyForQbo,
  ])

  const handleOverwriteApprovedDuplicateClick = () => {
    if (!onOverwriteApprovedDuplicate || !canOverwriteApprovedDuplicate) return
    const entityLabel =
      duplicateApprovedQboEntityType === 'VendorCredit' ? 'vendor credit' : 'bill'
    const peerBkDoc =
      duplicateApprovedDocumentId != null &&
      duplicateApprovedDocumentId !== document.id
    const confirmMsg = peerBkDoc
      ? `This will permanently replace the existing QuickBooks ${entityLabel}'s fields and line items with this document's data, attach this document's PDF, and archive the previous approved copy in Bookkeeper. Continue?`
      : `This will permanently replace the existing QuickBooks ${entityLabel}'s fields and line items with this document's data and attach this document's PDF. Continue?`
    if (!confirm(confirmMsg)) {
      return
    }
    onOverwriteApprovedDuplicate()
  }

  const reviewFlags = useMemo(() => {
    const flags: string[] = []

    // Server-computed extracted-total warnings (list view / API). Must be merged here —
    // omitting these left Approve enabled with no confirm when the flag was only on needs_review_flags.
    for (const f of document.needs_review_flags ?? []) {
      if (f.startsWith('total_validation_mismatch:')) {
        flags.push(f)
      }
      if (f === 'unallocated_shared_costs' && sharedCostAllocationActionable) {
        flags.push(f)
      }
    }
    if (sharedCostAllocationActionable && !flags.includes('unallocated_shared_costs')) {
      flags.push('unallocated_shared_costs')
    }

    if (vendorPickInvalid) {
      flags.push('missing_vendor')
    }
    if (!formData.doc_type) {
      flags.push('missing_doc_type')
    }
    if (!formData.location_id) {
      flags.push('missing_location')
    }
    const categoryGateLineItems =
      lineItems.length > 0 ? lineItems : mapApiLineItemsToFormState(document.line_items ?? [])
    if (
      categoryGateLineItems.length > 0 &&
      linesMissingRequiredCategoriesForSubmit(formData.doc_type, categoryGateLineItems) &&
      !flags.includes('missing_categories')
    ) {
      flags.push('missing_categories')
    }

    if (formData.doc_type === 'bill' && (!formData.issue_date || !formData.due_date)) {
      flags.push('missing_bill_dates')
    }

    if (document.needs_review_flags?.includes('non_payable_notice')) {
      flags.push('non_payable_notice')
    }

    if (
      docTypeRequiresLineItems(formData.doc_type) &&
      lineItems.length === 0 &&
      !document.needs_review_flags?.includes('non_payable_notice')
    ) {
      flags.push('missing_line_items')
    }

    // Extracted total vs current line total (when API did not already flag validation mismatch).
    if (
      !flags.some((f) => f.startsWith('total_validation_mismatch:')) &&
      extractedVsLineItemsCentsDiff != null &&
      extractedVsLineItemsCentsDiff >= 1
    ) {
      flags.push(
        `total_validation_mismatch:$${(extractedVsLineItemsCentsDiff / 100).toFixed(2)}`,
      )
    }

    if (document.needs_review_flags?.includes('qbo_duplicate')) {
      flags.push('qbo_duplicate')
    }

    const unallocIdx = flags.indexOf('unallocated_shared_costs')
    if (unallocIdx >= 0) {
      flags.splice(unallocIdx, 1)
      flags.push('unallocated_shared_costs')
    }

    return flags
  }, [
    vendorPickInvalid,
    formData.doc_type,
    formData.location_id,
    formData.issue_date,
    formData.due_date,
    lineItems,
    lineItemTotal,
    document.needs_review_flags,
    sharedCostAllocationActionable,
    document.total_amount,
    extractedVsLineItemsCentsDiff,
  ])

  const amberReviewFlags = useMemo(() => {
    const fromApi = getAmberReviewFlagsFromNeedsReviewFlags(document.needs_review_flags ?? [])
    const fromForm = reviewFlags.filter(
      (f) => f !== 'qbo_duplicate' && !isBlockingReviewFlag(f),
    )
    const merged = [...fromApi]
    for (const f of fromForm) {
      if (!merged.includes(f)) {
        merged.push(f)
      }
    }
    return merged
  }, [document.needs_review_flags, reviewFlags])

  const allocateSharedCostsButtonState = useMemo(() => {
    const missingCats = linesMissingRequiredCategoriesForSubmit(formData.doc_type, lineItems)
    return {
      disabled: allocatingSharedCosts || missingCats,
      title: allocatingSharedCosts
        ? 'Allocating…'
        : missingCats
          ? 'Assign a QuickBooks category to every product line (rows that are not shared-cost lines such as tax, shipping, discount, tip, fees, Express, or personal shopper) before allocating shared costs. Those aggregate lines do not need a category for this step.'
          : 'Spread shared costs (tax, shipping, discount, tip, fees, personal shopper, lines whose description contains the word Express, etc.) across expense categories (pro rata)',
    }
  }, [lineItems, allocatingSharedCosts, formData.doc_type])

  const approveButtonTitle = useMemo(() => {
    if (canApprove && !hasNonPayableNotice) return 'Approve and send to QuickBooks'
    if (canApprove && hasNonPayableNotice) {
      return 'This document was flagged as not payable. Click to review and override if intended.'
    }
    if (vendorPickInvalid) {
      return 'Choose a valid QuickBooks vendor before sending to QuickBooks.'
    }
    if (!formData.location_id) {
      return 'Choose a location before sending to QuickBooks.'
    }
    if (formData.doc_type === 'bill' && (!formData.issue_date || !formData.due_date)) {
      return 'Bills require both an issue date and a due date before sending to QuickBooks.'
    }
    if (isCardDocType(formData.doc_type) && !formData.issue_date) {
      return 'Card charges require a charge date before sending to QuickBooks.'
    }
    if (
      docTypeRequiresLineItems(formData.doc_type) &&
      lineItems.length === 0
    ) {
      return 'Add at least one line item before sending to QuickBooks.'
    }
    if (sharedCostAllocationActionable) {
      return 'Allocate shared costs before sending to QuickBooks.'
    }
    if (hasSharedCostSourcesForAllocation && !hasProductLinesForSharedAllocation) {
      return 'Every line is classified as tax/shipping/discount/Express/etc., so shared costs cannot be spread. Add a regular expense line or fix mis-tagged rows, then allocate if needed.'
    }
    return 'Complete all required fields and assign categories'
  }, [
    canApprove,
    hasNonPayableNotice,
    vendorPickInvalid,
    formData.doc_type,
    formData.location_id,
    formData.issue_date,
    formData.due_date,
    lineItems.length,
    sharedCostAllocationActionable,
    hasSharedCostSourcesForAllocation,
    hasProductLinesForSharedAllocation,
  ])

  const warningFlags = useMemo(() => {
    return reviewFlags.filter(f =>
      f.startsWith('total_validation_mismatch:') ||
      f === 'qbo_duplicate' ||
      f === 'non_payable_notice'
    )
  }, [reviewFlags])

  /** Informational only; not part of needs_review_flags or Doc Review tables. */
  const showFixedAssetCapitalizationReminder = useMemo(() => {
    if (formData.doc_type !== 'bill') return false
    return lineItems.some((li) => {
      const n = parseFloat(String(li.amount))
      return Number.isFinite(n) && n >= FIXED_ASSET_LINE_AMOUNT_THRESHOLD
    })
  }, [formData.doc_type, lineItems])

  const categoryOptions: SelectOption[] = useMemo(
    () =>
      categories.map((c) => ({
        id: c.id,
        label: c.name,
        group: c.account_type || 'Other',
      })),
    [categories],
  )

  const vendorOptions: SelectOption[] = useMemo(
    () =>
      vendors.map((v) => ({
        id: v.display_name,
        label: v.display_name,
      })),
    [vendors],
  )

  function formatFlagMessage(flag: string): string {
    if (flag.startsWith('total_validation_mismatch:')) {
      const amount = flag.split(':')[1]
      return `Extracted total differs from entered total by ${amount}`
    }
    if (flag === 'qbo_duplicate') {
      if (amazonReportLocked) {
        const amt = formatLineItemCurrency(parseFloat(document.total_amount || '0') || 0)
        const date = formatCalendarDate(formData.issue_date) || '—'
        return amazonQboDuplicateMessage(document.extracted_data, {
          total: amt,
          issueDate: date,
        }).text
      }
      return 'QuickBooks already has a bill or vendor credit with this vendor and reference (DocNumber)'
    }
    if (flag === 'non_payable_notice') {
      const reason = typeof document.extracted_data?.non_payable_notice_reason === 'string'
        ? document.extracted_data.non_payable_notice_reason.trim()
        : null
      return reason || 'Document flagged with "do not pay" or "not an invoice" language'
    }
    return flag
  }

  /** Human-readable line for needs-review flags in the document panel (excludes `qbo_duplicate`, which is shown as a blocking error). */
  function formatReviewFlagLine(flag: string): string {
    if (flag.startsWith('total_validation_mismatch:')) {
      return `Total mismatch: ${flag.split(':')[1]}`
    }
    if (flag === 'missing_vendor') return 'Missing vendor'
    if (flag === 'missing_doc_type') return 'Missing type'
    if (flag === 'missing_location') return 'Missing location'
    if (flag === 'missing_categories') return 'Missing categories'
    if (flag === 'missing_bill_dates') {
      return 'Missing issue date or due date (required for bills)'
    }
    if (flag === 'missing_line_items') {
      return 'No line items — add at least one line before sending to QuickBooks'
    }
    if (flag === 'non_payable_notice') {
      const reason =
        typeof document.extracted_data?.non_payable_notice_reason === 'string'
          ? document.extracted_data.non_payable_notice_reason.trim()
          : ''
      if (reason) {
        return `${reason} You can override this by clicking Approve.`
      }
      return (
        'This document was flagged with not-an-invoice or do-not-pay language. ' +
        'You can override this by clicking Approve.'
      )
    }
    if (flag === 'unallocated_shared_costs') {
      return 'Unallocated shared costs — use Allocate shared costs below'
    }
    return getReviewFlagLabel(flag)
  }

  async function handleApproveClick() {
    if (!canApproveOrArchive || !canApprove) return
    const ae = globalThis.document?.activeElement ?? null
    if (
      ae instanceof HTMLElement &&
      panelRef.current?.contains(ae)
    ) {
      ae.blur()
    }
    await new Promise((r) => setTimeout(r, 0))
    await flushPendingSave()

    if (hasQboDuplicateFlag) {
      if (amazonReportLocked) {
        const amt = formatLineItemCurrency(parseFloat(document.total_amount || '0') || 0)
        const date = formatCalendarDate(formData.issue_date) || '—'
        const { confirmText } = amazonQboDuplicateMessage(document.extracted_data, {
          total: amt,
          issueDate: date,
        })
        if (!confirm(confirmText)) {
          return
        }
      } else {
        const amt = formatLineItemCurrency(parseFloat(document.total_amount || '0') || 0)
        const date = formatCalendarDate(formData.issue_date) || '—'
        if (
          !confirm(
            `QuickBooks already has a ${amt} Amazon Business expense on ${date} — post anyway?`,
          )
        ) {
          return
        }
      }
    }

    const nonDuplicateWarningFlags = warningFlags.filter((f) => f !== 'qbo_duplicate')
    if (nonDuplicateWarningFlags.length > 0) {
      setShowApproveConfirm(true)
    } else {
      onApprove(buildApproveOptions())
    }
  }

  function buildApproveOptions():
    | { overrideNonPayable?: boolean; overrideQboDuplicate?: boolean }
    | undefined {
    const opts: { overrideNonPayable?: boolean; overrideQboDuplicate?: boolean } = {}
    if (hasNonPayableNotice) opts.overrideNonPayable = true
    if (hasQboDuplicateFlag) opts.overrideQboDuplicate = true
    return Object.keys(opts).length > 0 ? opts : undefined
  }

  async function handleConfirmApprove() {
    if (!canApproveOrArchive || !canApprove) return
    setShowApproveConfirm(false)
    const ae = globalThis.document?.activeElement ?? null
    if (
      ae instanceof HTMLElement &&
      panelRef.current?.contains(ae)
    ) {
      ae.blur()
    }
    await new Promise((r) => setTimeout(r, 0))
    await flushPendingSave()
    onApprove(buildApproveOptions())
  }

  const handleDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!overlayRef.current) return
      const overlayRect = overlayRef.current.getBoundingClientRect()
      const totalWidth = overlayRect.width
      const relativeX = e.clientX - overlayRect.left
      let newPercent = relativeX / totalWidth

      const minDetailsPercent = MIN_DETAILS_WIDTH / totalWidth
      const minPdfPercent = MIN_PDF_WIDTH / totalWidth
      const maxDetailsPercent = 1 - minPdfPercent

      newPercent = Math.max(minDetailsPercent, Math.min(maxDetailsPercent, newPercent))
      setDetailsWidthPercent(newPercent)
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (
        showRefreshModal ||
        showApproveConfirm ||
        showBulkCategoryModal ||
        showEmailRuleEdit ||
        showStrongEnrichModal
      )
        return
      
      const target = e.target as HTMLElement
      const isInputField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable
      
      if (e.key === 'Escape') {
        void handleClose()
      } else if (e.key === 'ArrowUp' && hasPrev && onNavigatePrev && !isInputField) {
        e.preventDefault()
        onNavigatePrev()
      } else if (e.key === 'ArrowDown' && hasNext && onNavigateNext && !isInputField) {
        e.preventDefault()
        onNavigateNext()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    handleClose,
    showRefreshModal,
    showApproveConfirm,
    showBulkCategoryModal,
    showEmailRuleEdit,
    showStrongEnrichModal,
    hasPrev,
    hasNext,
    onNavigatePrev,
    onNavigateNext,
  ])

  useEffect(() => {
    setOcrCopied(false)
    setPreviewMode(pickDefaultDocumentPreviewMode(document))
  }, [
    document.id,
    document.pdf_storage_path,
    document.raw_ocr_text,
    document.source_type,
    document.extracted_data,
  ])

  async function handleCopyOcrText() {
    if (!ocrPreviewText) return
    try {
      await navigator.clipboard.writeText(ocrPreviewText)
      setOcrCopied(true)
      setTimeout(() => setOcrCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy OCR text:', err)
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 md:py-2"
      onClick={(e) => {
        if (e.target === e.currentTarget) void handleClose()
      }}
    >
      <div
        ref={overlayRef}
        className="relative bg-white shadow-2xl flex w-full min-h-[100dvh] h-full flex-col overflow-hidden md:min-h-0 md:h-[80vh] md:w-[90vw] md:max-w-[1600px] md:rounded-lg md:flex-row"
        onClick={(e) => e.stopPropagation()}
      >
        {showPreviewPanel && (
          <div className="flex shrink-0 border-b border-gray-200 bg-slate-100/90 md:hidden">
            <button
              type="button"
              onClick={() => setMobileDocTab('details')}
              className={`flex-1 min-h-11 px-3 py-2.5 text-sm font-medium transition-colors ${
                mobileDocTab === 'details'
                  ? 'bg-white text-blue-700 shadow-sm border-b-2 border-blue-600 -mb-px'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Details
            </button>
            <button
              type="button"
              onClick={() => {
                setPreviewSplitOpen(true)
                setMobileDocTab('preview')
              }}
              className={`flex-1 min-h-11 px-3 py-2.5 text-sm font-medium transition-colors ${
                mobileDocTab === 'preview'
                  ? 'bg-white text-blue-700 shadow-sm border-b-2 border-blue-600 -mb-px'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Preview
            </button>
          </div>
        )}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden md:flex-row">
        {/* Main Panel */}
        <div
          ref={panelRef}
          className={`min-h-0 flex flex-shrink-0 flex-col overflow-hidden bg-white h-full md:h-auto md:flex-shrink ${
            showSplitPreview && mobileDocTab === 'preview' ? 'hidden md:flex' : 'flex'
          }`}
          style={{
            ...(!maxMd && showSplitPreview
              ? { width: `${detailsWidthPercent * 100}%` }
              : { width: '100%' }),
          }}
        >
        {/* Header */}
        <div className="flex items-center justify-between px-3 h-12 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-slate-50">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => void handleClose()}
              className="btn btn-ghost p-1 text-blue-700 hover:bg-blue-100 flex-shrink-0"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <h3 className="text-sm sm:text-base font-semibold text-slate-800 truncate">Document Details</h3>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {showPreviewPanel && maxMd && (
              <button
                type="button"
                onClick={() => setPreviewSplitOpen((o) => !o)}
                className="btn btn-ghost p-1.5 text-slate-600 hover:bg-blue-100"
                title={previewSplitOpen ? 'Hide preview' : 'Show preview'}
                aria-expanded={previewSplitOpen}
              >
                {previewSplitOpen ? (
                  <ChevronRightIcon className="w-5 h-5" />
                ) : (
                  <DocumentTextIcon className="w-5 h-5" />
                )}
              </button>
            )}
            {document.email_rule_id != null && canEdit && (
              <button
                type="button"
                onClick={() => setShowEmailRuleEdit(true)}
                className="btn btn-ghost p-1.5 text-slate-600 hover:bg-blue-100"
                title={emailRuleEditTitle}
                aria-label={emailRuleEditTitle}
              >
                <EnvelopeIcon className="w-5 h-5" />
              </button>
            )}
            {qboVendorId && (
              <a
                href={qboVendorDetailUrl(qboVendorId)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-ghost p-1.5 text-slate-600 hover:bg-blue-100"
                title="Open vendor in QuickBooks"
                aria-label="Open vendor in QuickBooks"
              >
                <ArrowTopRightOnSquareIcon className="w-5 h-5" />
              </a>
            )}
            <button
              onClick={onNavigatePrev}
              disabled={!hasPrev}
              className="btn btn-ghost p-1.5 text-slate-600 hover:bg-blue-100 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Previous document (↑)"
            >
              <ChevronUpIcon className="w-5 h-5" />
            </button>
            <button
              onClick={onNavigateNext}
              disabled={!hasNext}
              className="btn btn-ghost p-1.5 text-slate-600 hover:bg-blue-100 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Next document (↓)"
            >
              <ChevronDownIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content - split into fixed header and scrollable line items */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Fixed header section */}
          <div className="flex-shrink-0 space-y-3 overflow-y-auto border-b border-gray-200 bg-slate-50/50 p-3 sm:space-y-4 sm:p-4 max-md:max-h-[min(50vh,24rem)] md:max-h-none md:overflow-y-visible">
            {/* Status indicators */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {saving && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <ArrowPathIcon className="w-3 h-3 animate-spin" />
                    Saving...
                  </span>
                )}
                {!saving && lastSaved && (
                  <span className="text-xs text-gray-400">
                    Auto-saved
                  </span>
                )}
              </div>
            </div>

            {/* Blocking / errors — single container above warnings */}
            {(error ||
              approvalError ||
              overwriteError ||
              duplicateMatchesApproved ||
              reviewFlags.includes('qbo_duplicate') ||
              reviewFlags.includes('missing_line_items') ||
              reviewFlags.includes('missing_vendor') ||
              reviewFlags.includes('missing_location') ||
              reviewFlags.includes('missing_bill_dates') ||
              reviewFlags.includes('missing_categories') ||
              reviewFlags.includes('unallocated_shared_costs')) && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <div className="flex items-start gap-2.5">
                  <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-red-800">Errors</p>
                    <ul className="mt-2 space-y-2">
                      {error && (
                        <li className="flex items-start gap-2 text-xs text-red-700">
                          <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500" />
                          <span>{error}</span>
                        </li>
                      )}
                      {approvalError && (
                        <li className="flex items-start justify-between gap-2 text-xs text-red-700">
                          <div className="flex items-start gap-2">
                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500" />
                            <span>{approvalError}</span>
                          </div>
                          {onClearApprovalError && (
                            <button
                              type="button"
                              onClick={onClearApprovalError}
                              className="shrink-0 rounded p-0.5 text-red-500 hover:bg-red-100 hover:text-red-700"
                            >
                              <XMarkIcon className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </li>
                      )}
                      {reviewFlags.includes('qbo_duplicate') && (
                        <li className="flex items-start gap-2 text-xs text-red-700">
                          <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500" />
                          {amazonReportLocked ? (
                            <span>
                              {(() => {
                                const amt = formatLineItemCurrency(
                                  parseFloat(document.total_amount || '0') || 0,
                                )
                                const date = formatCalendarDate(formData.issue_date) || '—'
                                const dup = amazonQboDuplicateMessage(document.extracted_data, {
                                  total: amt,
                                  issueDate: date,
                                })
                                return (
                                  <>
                                    {dup.text}{' '}
                                    {dup.qboUrl ? (
                                      <a
                                        href={dup.qboUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-medium underline"
                                      >
                                        Open in QuickBooks
                                      </a>
                                    ) : null}
                                  </>
                                )
                              })()}
                            </span>
                          ) : (
                            <span>
                              QuickBooks already has a bill or vendor credit with this vendor and reference. Approve is blocked
                              until you change vendor or reference or clear the duplicate in QuickBooks.
                            </span>
                          )}
                        </li>
                      )}
                      {reviewFlags.includes('missing_line_items') && (
                        <li className="flex items-start gap-2 text-xs text-red-700">
                          <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500" />
                          <span>
                            This bill or vendor credit has no line items. Add at least one line with an amount before sending
                            to QuickBooks.
                          </span>
                        </li>
                      )}
                      {reviewFlags.includes('missing_vendor') && (
                        <li className="flex items-start gap-2 text-xs text-red-700">
                          <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500" />
                          <span>
                            {(formData.vendor_name ?? '').trim()
                              ? `Vendor "${formData.vendor_name}" was not found in QuickBooks. Select an active QuickBooks vendor before sending.`
                              : 'Vendor is required. Choose a QuickBooks vendor before sending to QuickBooks.'}{' '}
                            Approve is blocked until a valid vendor is selected.
                          </span>
                        </li>
                      )}
                      {reviewFlags.includes('missing_location') && (
                        <li className="flex items-start gap-2 text-xs text-red-700">
                          <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500" />
                          <span>
                            Location is required. Choose a location for this document before sending to QuickBooks.
                          </span>
                        </li>
                      )}
                      {reviewFlags.includes('missing_bill_dates') && (
                        <li className="flex items-start gap-2 text-xs text-red-700">
                          <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500" />
                          <span>
                            Bills require both an issue date and a due date. Set both dates before sending to QuickBooks.
                          </span>
                        </li>
                      )}
                      {reviewFlags.includes('missing_categories') && (
                        <li className="flex items-start gap-2 text-xs text-red-700">
                          <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500" />
                          <span>
                            One or more line items are missing a QuickBooks category. Assign categories to product lines (tax, shipping, discount, and similar shared-cost lines can be categorized when you allocate shared costs).{' '}
                            Approve is blocked until all required categories are assigned.
                          </span>
                        </li>
                      )}
                      {reviewFlags.includes('unallocated_shared_costs') && (
                        <li className="flex items-start gap-2 text-xs text-red-700">
                          <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500" />
                          <span>
                            This document has unallocated shared costs (tax, shipping, discount, tip, fees, personal
                            shopper, Express lines, etc.). Use Allocate shared costs below before sending to QuickBooks.
                          </span>
                        </li>
                      )}
                      {duplicateMatchesApproved && (
                        <li className="flex items-start gap-2 text-xs text-red-700">
                          <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500" />
                          <div className="min-w-0 flex-1 space-y-2">
                            <p>
                              {duplicateApprovedDocumentId != null &&
                              duplicateApprovedDocumentId !== document.id ? (
                                <>
                                  <span className="font-medium">
                                    Already approved in Bookkeeper.
                                  </span>{' '}
                                  This vendor and reference match a document already in
                                  QuickBooks. Approve is blocked—you can open the existing
                                  record or replace it with this version.
                                </>
                              ) : (
                                <>
                                  <span className="font-medium">Already in QuickBooks.</span>{' '}
                                  This vendor and reference match a bill or vendor credit in
                                  QuickBooks. Approve is blocked—you can open the existing
                                  record or replace it with this version.
                                </>
                              )}
                            </p>

                            {(duplicateApprovedQboUrl ||
                              (onOverwriteApprovedDuplicate && duplicateApprovedQboEntityId)) && (
                              <div className="flex flex-wrap gap-1.5">
                                {duplicateApprovedQboUrl && (
                                  <a
                                    href={duplicateApprovedQboUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 rounded border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-50"
                                  >
                                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 shrink-0" />
                                    {formatViewInQboLabel(
                                      duplicateApprovedLineItemCount,
                                      duplicateApprovedTotalAmount,
                                    )}
                                  </a>
                                )}
                                {onOverwriteApprovedDuplicate &&
                                  duplicateApprovedQboEntityId &&
                                  !duplicateApprovedQboTypeMismatch &&
                                  !isReadOnly && (
                                    <button
                                      type="button"
                                      onClick={handleOverwriteApprovedDuplicateClick}
                                      disabled={
                                        !canOverwriteApprovedDuplicate || isOverwriting || isApproving
                                      }
                                      title={overwriteDisabledReason ?? undefined}
                                      className="inline-flex items-center gap-1.5 rounded border border-red-300 bg-red-700 px-2 py-1 text-xs font-medium text-white transition-colors hover:bg-red-800 disabled:cursor-not-allowed disabled:border-red-200 disabled:bg-red-200 disabled:text-red-500"
                                    >
                                      {isOverwriting ? (
                                        <>
                                          <ArrowPathIcon className="h-3.5 w-3.5 shrink-0 animate-spin" />
                                          Replacing…
                                        </>
                                      ) : (
                                        <>
                                          <ArrowsRightLeftIcon className="h-3.5 w-3.5 shrink-0" />
                                          Replace in QBO
                                        </>
                                      )}
                                    </button>
                                  )}
                              </div>
                            )}

                            {overwriteDisabledReason && !canOverwriteApprovedDuplicate && (
                              <p className="text-xs text-red-700">{overwriteDisabledReason}</p>
                            )}

                            {overwriteError && (
                              <div className="flex items-start justify-between gap-2 rounded border border-red-200 bg-white/60 px-2 py-1.5">
                                <p className="text-xs text-red-700">{overwriteError}</p>
                                {onClearOverwriteError && (
                                  <button
                                    type="button"
                                    onClick={onClearOverwriteError}
                                    className="shrink-0 rounded p-0.5 text-red-500 hover:bg-red-100 hover:text-red-700"
                                    aria-label="Dismiss"
                                  >
                                    <XMarkIcon className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Warnings — single container below errors, above metadata-adjacent content */}
            {(isPendingDuplicate || amberReviewFlags.length > 0) &&
              (maxMd && amberReviewFlags.length > 0 ? (
                <details className="rounded-lg border border-amber-200 bg-amber-50">
                  <summary className="cursor-pointer list-none px-3 py-2.5 text-xs font-semibold text-amber-800 [&::-webkit-details-marker]:hidden">
                    <span className="flex items-center gap-2">
                      <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-amber-600" />
                      Warnings ({(isPendingDuplicate ? 1 : 0) + amberReviewFlags.length})
                    </span>
                  </summary>
                  <ul className="space-y-2 border-t border-amber-200/60 px-3 pb-3 pt-2">
                    {isPendingDuplicate && (
                      <li className="flex items-start gap-2 text-xs text-amber-700">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500" />
                        <span>Another pending document has the same vendor and reference number.</span>
                      </li>
                    )}
                    {amberReviewFlags.map((flag, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-amber-700">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500" />
                        <span>{formatReviewFlagLine(flag)}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="flex items-start gap-2.5">
                    <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-amber-800">Warnings</p>
                      <ul className="mt-2 space-y-2">
                        {isPendingDuplicate && (
                          <li className="flex items-start gap-2 text-xs text-amber-700">
                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500" />
                            <span>Another pending document has the same vendor and reference number.</span>
                          </li>
                        )}
                        {amberReviewFlags.map((flag, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-xs text-amber-700">
                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500" />
                            <span>{formatReviewFlagLine(flag)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}

            {/* Fixed asset reminder — informational; does not block Approve or appear on Doc Review rows */}
            {showFixedAssetCapitalizationReminder && (
              <div className="rounded-lg border border-sky-200 bg-sky-50 p-2.5 shadow-sm">
                <div className="flex gap-2">
                  <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-sky-600" aria-hidden />
                  <div>
                    <p className="text-xs font-medium text-sky-900">Fixed asset check</p>
                    <p className="mt-0.5 text-xs leading-snug text-sky-800">
                      At least one line item is {formatLineItemCurrency(FIXED_ASSET_LINE_AMOUNT_THRESHOLD)} or more. That
                      line may need to be capitalized as a Fixed Asset rather than expensed—confirm against your
                      capitalization policy.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {amountExceedsEmailRuleThreshold && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 shadow-sm">
                <div className="flex gap-2">
                  <ExclamationTriangleIcon
                    className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
                    aria-hidden
                  />
                  <div>
                    <p className="text-xs font-medium text-amber-900">Higher than expected amount</p>
                    <p className="mt-0.5 text-xs leading-snug text-amber-800">
                      This bill&apos;s total ({formatLineItemCurrency(lineItemTotal)}) is above the alert
                      limit you set on the matching email rule (
                      {formatLineItemCurrency(
                        parseFloat(String(document.amount_alert_threshold)),
                      )}
                      ). Take a moment to confirm the amount before approving.
                      {document.email_rule_id != null && canEdit && (
                        <>
                          {' '}
                          <button
                            type="button"
                            onClick={() => setShowEmailRuleEdit(true)}
                            className="font-medium text-amber-900 underline decoration-amber-400 underline-offset-2 hover:text-amber-950"
                          >
                            Edit rule
                          </button>
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Approved status banner and actions */}
            {document.status === 'approved' && document.qbo_entity_id && (
              <div className="space-y-3">
                <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckIcon className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm font-medium">
                      Sent to QuickBooks as{' '}
                      <a
                        href={qboTxnUrl(document.qbo_entity_type, document.qbo_entity_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline text-green-800 hover:text-green-900"
                      >
                        {document.qbo_entity_type === 'VendorCredit' ? 'Vendor Credit' : 'Bill'}
                      </a>
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <a
                    href={qboTxnUrl(document.qbo_entity_type, document.qbo_entity_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 btn bg-gray-100 border border-gray-300 text-gray-700 hover:bg-gray-200 flex items-center justify-center gap-2 text-sm"
                  >
                    <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                    View in QuickBooks
                  </a>
                  {onUnapprove && canEdit && (
                    <button
                      onClick={onUnapprove}
                      className="flex-1 btn bg-gray-100 border border-gray-300 text-gray-700 hover:bg-gray-200 flex items-center justify-center gap-2 text-sm"
                    >
                      <ArrowUturnLeftIcon className="w-4 h-4" />
                      Restore to Review
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Archived status banner and actions */}
            {document.status === 'deleted' && (
              <div className="space-y-3">
                <div className="bg-gray-100 border border-gray-300 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 text-gray-600">
                    <ArchiveBoxIcon className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm font-medium">This document has been archived</span>
                  </div>
                </div>
                {onRestore && canEdit && (
                  <button
                    onClick={onRestore}
                    className="w-full btn btn-secondary flex items-center justify-center gap-2 text-sm"
                  >
                    <ArrowUturnLeftIcon className="w-4 h-4" />
                    Restore to Review
                  </button>
                )}
              </div>
            )}

            {/* Collapsible vendor details section */}
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              {/* Clickable header - always visible */}
              <button
                type="button"
                onClick={() => setVendorDetailsCollapsed(!vendorDetailsCollapsed)}
                className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <ChevronDownIcon 
                    className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform duration-200 ${vendorDetailsCollapsed ? '' : 'rotate-180'}`} 
                  />
                  <span className="text-xs font-medium text-gray-700">Document Metadata</span>
                  {vendorDetailsCollapsed && (
                    <span className="text-xs text-gray-500 truncate">
                      {formData.vendor_name || 'No vendor'} 
                      {formData.doc_type && ` · ${docTypeLabel(formData.doc_type)}`}
                      {formData.location && ` · ${formData.location}`}
                    </span>
                  )}
                </div>
                {vendorDetailsCollapsed && !isReadOnly && (
                  <span className="text-xs text-blue-600 flex-shrink-0 ml-2">Edit</span>
                )}
              </button>

              {/* Expandable content */}
              {!vendorDetailsCollapsed && (
                <div className="p-3 border-t border-gray-200 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="sm:col-span-2">
                      <label className="label text-xs font-semibold text-slate-700">
                        Vendor Name
                        {!isReadOnly && <span className="text-red-500 ml-0.5">*</span>}
                      </label>
                      {typeVendorReadOnly ? (
                        <div className="input bg-gray-50 text-gray-700">{formData.vendor_name || '-'}</div>
                      ) : (
                        <>
                          <SearchableSelect
                            options={vendorOptions}
                            value={formData.vendor_name || null}
                            onChange={(_, label) => handleVendorChange(label)}
                            placeholder="Select or create vendor..."
                            disabled={loadingQBO}
                            allowCreate
                            onCreateNew={handleCreateVendor}
                            createLabel="Create vendor"
                            error={vendorPickInvalid}
                            useViewportForPlacement
                            preferBelow
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="label text-xs font-semibold text-slate-700">Type</label>
                      {typeVendorReadOnly ? (
                        <div className="input bg-gray-50 text-gray-700">
                          {formData.doc_type ? docTypeLabel(formData.doc_type) : '-'}
                        </div>
                      ) : (
                        <select
                          className={`input select ${!formData.doc_type ? METADATA_FIELD_ERROR_CLASS : ''}`}
                          value={formData.doc_type}
                          onChange={(e) => {
                            const v = e.target.value
                            setFormData((prev) =>
                              v === 'vendor_credit'
                                ? { ...prev, doc_type: v, qbo_sales_term_id: '' }
                                : { ...prev, doc_type: v },
                            )
                          }}
                          onBlur={() => {
                            flushPersistAfterFieldCommit()
                          }}
                        >
                          <option value="">Select...</option>
                          <option value="bill">Bill</option>
                          <option value="vendor_credit">Vendor Credit</option>
                          <option value="card_expense">Card expense</option>
                          <option value="card_credit">Card credit</option>
                        </select>
                      )}
                    </div>

                    <div>
                      <label className="label text-xs font-semibold text-slate-700">Location</label>
                      {isReadOnly ? (
                        <div className="input bg-gray-50 text-gray-700">{formData.location || '-'}</div>
                      ) : (
                        <select
                          className={`input select ${!formData.location_id ? METADATA_FIELD_ERROR_CLASS : ''}`}
                          value={formData.location_id}
                          onChange={(e) => handleLocationChange(e.target.value)}
                          onBlur={() => {
                            flushPersistAfterFieldCommit()
                          }}
                        >
                          <option value="">Select...</option>
                          {locations.map((loc) => (
                            <option key={loc.id} value={loc.id}>
                              {loc.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    {formData.doc_type === 'bill' ? (
                      <>
                        <div>
                          <label className="label text-xs">
                            Issue Date<span className="text-red-500 ml-0.5">*</span>
                          </label>
                          {isReadOnly ? (
                            <div className="input bg-gray-50 text-gray-700">
                              {formatCalendarDate(formData.issue_date)}
                            </div>
                          ) : (
                            <DateInput
                              className={`input ${!formData.issue_date ? METADATA_FIELD_ERROR_CLASS : ''}`}
                              value={formData.issue_date}
                              dateFormat="iso"
                              onChange={(value) =>
                                setFormData((prev) => ({ ...prev, issue_date: value }))
                              }
                              onCommitBlur={() => {
                                flushPersistAfterFieldCommit()
                                void (async () => {
                                  const tid = formDataRef.current.qbo_sales_term_id.trim()
                                  const iss = formDataRef.current.issue_date.trim()
                                  if (!tid || !iss) return
                                  try {
                                    const d = await getTermDueDatePreview(tid, iss)
                                    if (d) {
                                      const next = {
                                        ...formDataRef.current,
                                        due_date: d,
                                      }
                                      formDataRef.current = next
                                      setFormData(next)
                                      flushPersistAfterFieldCommit()
                                    }
                                  } catch {
                                    /* ignore preview errors */
                                  }
                                })()
                              }}
                            />
                          )}
                        </div>
                        <div>
                          <label className="label text-xs">Terms</label>
                          {isReadOnly ? (
                            <div className="input bg-gray-50 text-gray-700">
                              {paymentTerms.find((t) => t.id === formData.qbo_sales_term_id)?.name ||
                                '—'}
                            </div>
                          ) : (
                            <select
                              className="input select"
                              value={formData.qbo_sales_term_id}
                              onChange={(e) => {
                                const v = e.target.value
                                void (async () => {
                                  const prev = formDataRef.current
                                  const iss = prev.issue_date.trim()
                                  let nextDue = prev.due_date
                                  if (v && iss) {
                                    try {
                                      const d = await getTermDueDatePreview(v, iss)
                                      if (d) nextDue = d
                                    } catch {
                                      /* ignore */
                                    }
                                  }
                                  const next = {
                                    ...prev,
                                    qbo_sales_term_id: v,
                                    due_date: nextDue,
                                  }
                                  // performSave reads formDataRef; sync before flush so autosave
                                  // is not scheduled before React's useEffect copies state into the ref.
                                  formDataRef.current = next
                                  setFormData(next)
                                  flushPersistAfterFieldCommit()
                                })()
                              }}
                              disabled={loadingQBO}
                            >
                              <option value="">None</option>
                              {paymentTerms.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                        <div>
                          <label className="label text-xs">
                            Due Date<span className="text-red-500 ml-0.5">*</span>
                          </label>
                          {isReadOnly ? (
                            <div className="input bg-gray-50 text-gray-700">
                              {formatCalendarDate(formData.due_date)}
                            </div>
                          ) : (
                            <DateInput
                              className={`input ${!formData.due_date ? METADATA_FIELD_ERROR_CLASS : ''}`}
                              value={formData.due_date}
                              dateFormat="iso"
                              onChange={(value) =>
                                setFormData((prev) => ({ ...prev, due_date: value }))
                              }
                              onCommitBlur={() => {
                                flushPersistAfterFieldCommit()
                              }}
                            />
                          )}
                        </div>
                        <div>
                          <label className="label text-xs">Received</label>
                          <div className="input bg-gray-50 text-gray-700 text-sm">
                            {formatLocalDateTime(document.created_at)}
                          </div>
                        </div>
                      </>
                    ) : isCardDocType(formData.doc_type) ? (
                      <>
                        <div>
                          <label className="label text-xs">
                            Charge date<span className="text-red-500 ml-0.5">*</span>
                          </label>
                          {isReadOnly ? (
                            <div className="input bg-gray-50 text-gray-700">
                              {formatCalendarDate(formData.issue_date)}
                            </div>
                          ) : (
                            <DateInput
                              className={`input ${!formData.issue_date ? METADATA_FIELD_ERROR_CLASS : ''}`}
                              value={formData.issue_date}
                              dateFormat="iso"
                              onChange={(value) =>
                                setFormData((prev) => ({ ...prev, issue_date: value }))
                              }
                              onCommitBlur={() => {
                                flushPersistAfterFieldCommit()
                              }}
                            />
                          )}
                        </div>
                        <div>
                          <label className="label text-xs">Received</label>
                          <div className="input bg-gray-50 text-gray-700 text-sm">
                            {formatLocalDateTime(document.created_at)}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <label className="label text-xs">Issue Date</label>
                          {isReadOnly ? (
                            <div className="input bg-gray-50 text-gray-700">
                              {formatCalendarDate(formData.issue_date)}
                            </div>
                          ) : (
                            <DateInput
                              className="input"
                              value={formData.issue_date}
                              dateFormat="iso"
                              onChange={(value) =>
                                setFormData((prev) => ({ ...prev, issue_date: value }))
                              }
                              onCommitBlur={() => {
                                flushPersistAfterFieldCommit()
                              }}
                            />
                          )}
                        </div>
                        <div>
                          <label className="label text-xs">Received</label>
                          <div className="input bg-gray-50 text-gray-700 text-sm">
                            {formatLocalDateTime(document.created_at)}
                          </div>
                        </div>
                      </>
                    )}

                    <div>
                      <label className="label text-xs">Reference #</label>
                      {isReadOnly ? (
                        <div className="input bg-gray-50 text-gray-700">{formData.reference_number || '-'}</div>
                      ) : (
                        <input
                          type="text"
                          className="input"
                          value={formData.reference_number}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              reference_number: e.target.value,
                            }))
                          }
                          onBlur={() => {
                            flushPersistAfterFieldCommit()
                          }}
                        />
                      )}
                    </div>

                    <div className="sm:col-span-1">
                      <label className="label text-xs">Memo</label>
                      {isReadOnly ? (
                        <div className="input bg-gray-50 text-gray-700">{formData.memo || '-'}</div>
                      ) : (
                        <input
                          type="text"
                          className="input"
                          value={formData.memo}
                          placeholder="Optional..."
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, memo: e.target.value }))
                          }
                          onBlur={() => {
                            flushPersistAfterFieldCommit()
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Total Amount - below collapsible section */}
            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <span className="text-xs font-medium text-blue-700">Total Amount</span>
              <div className="text-right">
                <span className="text-base font-semibold text-blue-900">
                  {formatLineItemCurrency(lineItemTotal)}
                </span>
                {document.extracted_total &&
                  extractedVsLineItemsCentsDiff != null &&
                  extractedVsLineItemsCentsDiff >= 1 && (
                  <div className="flex items-center justify-end gap-1 text-xs text-amber-600 mt-0.5">
                    <ExclamationTriangleIcon className="w-3 h-3" />
                    <span>Doc shows: {formatLineItemCurrency(parseFloat(document.extracted_total))}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Line Items header */}
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-1.5">
                <label className="label text-xs mb-0 font-semibold">Line Items</label>
                {document.status === 'approved' && (
                  <InformationCircleIcon
                    className="w-4 h-4 text-gray-400 cursor-help"
                    title="Categories shown may differ from QuickBooks if modified after this document was submitted"
                  />
                )}
              </div>
              <div className="flex max-w-full flex-wrap items-center justify-end gap-x-2 gap-y-1">
                {!isReadOnly && selectedLineRowKeys.size > 0 && (
                  <div
                    className="inline-flex items-stretch overflow-hidden rounded-lg border border-slate-200 bg-slate-50/90 shadow-sm"
                    role="group"
                    aria-label="Actions for selected line items"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setBulkCategoryError(null)
                        setBulkCategoryId(null)
                        setBulkCategoryName(null)
                        setShowBulkCategoryModal(true)
                      }}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-50"
                      title="Apply one category to all selected line items"
                    >
                      <TagIcon className="w-3.5 h-3.5 shrink-0" />
                      Set category ({selectedLineRowKeys.size})
                    </button>
                    {!amazonReportLocked ? (
                      <button
                        type="button"
                        onClick={() => {
                          setCorrectionModalPosition(null)
                          setShowCorrectionModal(true)
                        }}
                        className="inline-flex items-center gap-1 border-l border-slate-200 px-2.5 py-1.5 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-50/80"
                        title="Use AI to correct selected line items"
                      >
                        <ExclamationTriangleIcon className="w-3.5 h-3.5 shrink-0" />
                        Fix Selected ({selectedLineRowKeys.size})
                      </button>
                    ) : null}
                  </div>
                )}
                {canRunSkuEnrichment ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void handleRerunSkuEnrichment()}
                    disabled={enrichingSku}
                    title="Re-run COGS SKU enrichment for this document"
                  >
                    <ArrowPathIcon className={`h-3.5 w-3.5 shrink-0 ${enrichingSku ? 'animate-spin' : ''}`} />
                    Re-run SKU enrichment
                  </Button>
                ) : null}
                {hasEnrichmentFailedFlag ? (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowStrongEnrichModal(true)}
                    disabled={enrichingSku}
                    title="Extract for COGS ledger with a stronger model"
                  >
                    <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" />
                    Extract for COGS ledger
                  </Button>
                ) : null}
                {!isReadOnly && lineItems.length > 0 && formData.vendor_name && (
                  <button
                    type="button"
                    onClick={() => setShowRefreshModal(true)}
                    className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-800"
                    title="Re-infer categories based on vendor history"
                  >
                    <Squares2X2Icon className="w-3.5 h-3.5 shrink-0" />
                    Refresh
                  </button>
                )}
                {!isReadOnly && sharedCostAllocationActionable && (
                  <button
                    type="button"
                    onClick={() => void handleAllocateSharedCosts()}
                    disabled={allocateSharedCostsButtonState.disabled}
                    className="flex items-center gap-1 text-xs font-medium text-amber-700 transition-colors hover:text-amber-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={allocateSharedCostsButtonState.title}
                  >
                    <ArrowsRightLeftIcon className="w-3.5 h-3.5" />
                    {allocatingSharedCosts ? 'Allocating…' : 'Allocate shared costs'}
                  </button>
                )}
              </div>
            </div>

            {lineItems.length > 0 && !isReadOnly && maxMd && (
              <div className="flex items-center gap-2 pb-2">
                <input
                  ref={selectAllLineItemsMobileCheckboxRef}
                  type="checkbox"
                  checked={lineItemsAllSelected}
                  onChange={toggleSelectAllLineItems}
                  className="h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  aria-label="Select all line items"
                />
                <span className="text-xs text-gray-600">Select all line items</span>
              </div>
            )}

            {/* Column headers - sticky */}
            {lineItems.length > 0 && (
              <div
                className={`hidden gap-2 px-2 md:grid ${lineItemHeaderGridClass(isReadOnly, showAmazonLineQtyUnit)}`}
              >
                {!isReadOnly && (
                  <input
                    ref={selectAllLineItemsCheckboxRef}
                    type="checkbox"
                    checked={lineItemsAllSelected}
                    onChange={toggleSelectAllLineItems}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer self-center"
                    aria-label="Select all line items"
                    title="Select all line items"
                  />
                )}
                {!isReadOnly && <span aria-hidden="true" />}
                <span className="text-[10px] font-medium uppercase text-gray-500">Category</span>
                <span className="text-[10px] font-medium uppercase text-gray-500">Description</span>
                {showAmazonLineQtyUnit ? (
                  <>
                    <span className="text-right text-[10px] font-medium uppercase text-gray-500">Qty</span>
                    <span className="text-right text-[10px] font-medium uppercase text-gray-500">Unit price</span>
                  </>
                ) : null}
                <span className="text-right text-[10px] font-medium uppercase text-gray-500">Amount</span>
                {!isReadOnly && <span></span>}
              </div>
            )}
          </div>

          {/* Scrollable line items section */}
          <div className="flex-1 overflow-y-auto p-4 pt-2 max-md:pb-2">
            <DndContext
              key={maxMd ? 'narrow' : 'wide'}
              sensors={dndSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={lineItems.map((item) => item.rowKey)}
                strategy={verticalListSortingStrategy}
              >
                <div className="max-md:space-y-2 md:space-y-1">
                  {lineItems.map((item, index) => (
                    <SortableLineItem
                      key={item.rowKey}
                      item={item}
                      index={index}
                      isReadOnly={isReadOnly}
                      isSelected={selectedLineRowKeys.has(item.rowKey)}
                      isMaxMd={maxMd}
                      categoryRequired={lineItemRequiresCategory(formData.doc_type, item, lineItems)}
                      categoryOptions={categoryOptions}
                      loadingQBO={loadingQBO}
                      onCategoryChange={handleCategoryChange}
                      onUpdateLineItem={updateLineItem}
                      onRemoveLineItem={removeLineItem}
                      onAddLineItemAt={addLineItemAt}
                      onLineItemSelectionCheckboxMouseDown={handleLineItemSelectionCheckboxMouseDown}
                      onLineItemSelectionCheckboxChange={handleLineItemSelectionCheckboxChange}
                      formatAmount={formatLineItemAmount}
                      formatCurrency={formatLineItemCurrency}
                      onRowHover={debouncedSetHighlightData}
                      onSaveFlushAfterFieldBlur={flushPersistAfterFieldCommit}
                      skuExpanderEligible={skuExpanderEligible}
                      skuExpanded={expandedSkuLineKeys.has(item.rowKey)}
                      purchaseLine={purchaseLinesByLineId.get(item.id) ?? null}
                      purchaseLinesLoading={purchaseLinesLoading}
                      showSkuChevron={
                        skuExpanderEligible &&
                        (!purchaseLinesFetchAttempted || purchaseLinesByLineId.has(item.id))
                      }
                      onToggleSkuExpand={() => void handleToggleSkuExpand(item.rowKey, item.id)}
                      showAmazonQtyUnit={showAmazonLineQtyUnit}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {lineItems.length === 0 && !isReadOnly && (
              <div className="text-center py-6">
                <p className="text-gray-400 text-sm mb-2">No line items</p>
                <button
                  type="button"
                  onClick={addLineItem}
                  className="btn btn-secondary text-sm"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Line Item
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer - only show for pending_review */}
        {document.status === 'pending_review' && (
          <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:p-3 sm:pb-3">
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={onDelete}
                className="btn btn-danger px-2 sm:px-3"
                title="Archive"
                disabled={!canApproveOrArchive}
              >
                <ArchiveBoxIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Archive</span>
              </button>
              <button
                onClick={handleApproveClick}
                className="btn btn-primary flex-1 sm:flex-none"
                disabled={!canApproveOrArchive || !canApprove || saving || isApproving}
                title={approveButtonTitle}
              >
                {isApproving ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    <span className="hidden sm:inline">Approving...</span>
                  </>
                ) : (
                  <>
                    <CheckIcon className="w-4 h-4" />
                    {resolveApproveButtonLabel(document.source_type, formData.doc_type)}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

        {/* Draggable Divider - hidden on mobile */}
        {showSplitPreview && (
          <div
            className={`hidden md:block w-1.5 bg-gray-200 hover:bg-blue-400 cursor-col-resize flex-shrink-0 transition-colors ${isDragging ? 'bg-blue-500' : ''}`}
            onMouseDown={handleDividerMouseDown}
          />
        )}

        {/* Preview Panel — split with Details on md+; tab on small screens */}
        {showSplitPreview && (
          <div
            className={`min-h-0 flex-1 flex-col overflow-hidden bg-white ${
              mobileDocTab === 'details' ? 'hidden md:flex' : 'flex'
            }`}
          >
            <div className="flex items-center gap-2 px-3 h-12 border-b border-gray-200 bg-gray-50">
              <div className="p-1">
                <DocumentTextIcon className="w-5 h-5 text-gray-500" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">Preview</h3>
            </div>
            {/* Preview mode toggle */}
            <div className="flex-shrink-0 px-3 py-2 border-b border-gray-200 bg-white">
              <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
                {document.pdf_storage_path && (
                  <button
                    type="button"
                    onClick={() => setPreviewMode('pdf')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      previewMode === 'pdf'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Attachment
                  </button>
                )}
                {hasEmailPreviewTab && (
                  <button
                    type="button"
                    onClick={() => setPreviewMode('email')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      previewMode === 'email'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Email
                  </button>
                )}
                {document.raw_ocr_text && (
                  <button
                    type="button"
                    onClick={() => setPreviewMode('ocr')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      previewMode === 'ocr'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {isCsvUpload ? 'CSV' : 'OCR Text'}
                  </button>
                )}
                {hasAmazonPreviewPayload(document.extracted_data) && (
                  <button
                    type="button"
                    onClick={() => setPreviewMode('amazon')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      previewMode === 'amazon'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Amazon
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 bg-gray-100 p-2 overflow-hidden">
              {previewHydrating ? (
                <div className="flex h-full items-center justify-center text-sm text-gray-500">
                  Loading preview…
                </div>
              ) : (
                <>
                  {previewMode === 'amazon' && hasAmazonPreviewPayload(document.extracted_data) && (
                    <div className="h-full min-h-0 overflow-hidden rounded border border-gray-300 bg-white">
                      <AmazonChargePreview
                        extractedData={document.extracted_data}
                        locationLabel={document.location}
                        chargeIncomplete={amazonChargeIncomplete}
                      />
                    </div>
                  )}
                  {previewMode === 'pdf' && document.pdf_storage_path && (
                    <div className="h-full">
                      <PdfViewer
                        documentId={document.id}
                        highlightData={highlightData}
                        downloadFilename={attachmentDownloadFilename(document)}
                      />
                    </div>
                  )}
                  {previewMode === 'email' && hasEmailPreviewTab && (
                    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded border border-gray-300 bg-white">
                      <div className="flex-shrink-0 space-y-1.5 border-b border-gray-200 p-4 pb-3">
                        {document.email_subject && (
                          <div className="flex gap-2">
                            <span className="text-xs font-medium text-gray-500 w-16 flex-shrink-0">Subject:</span>
                            <span className="text-sm text-gray-900">{document.email_subject}</span>
                          </div>
                        )}
                        {document.email_from && (
                          <div className="flex gap-2">
                            <span className="text-xs font-medium text-gray-500 w-16 flex-shrink-0">From:</span>
                            <span className="text-sm text-gray-700">{document.email_from}</span>
                          </div>
                        )}
                        {document.email_inbox && (
                          <div className="flex gap-2">
                            <span className="text-xs font-medium text-gray-500 w-16 flex-shrink-0">To:</span>
                            <span className="text-sm text-gray-700">{document.email_inbox}</span>
                          </div>
                        )}
                        {emailHeaderDateTime && (
                          <div className="flex gap-2">
                            <span className="text-xs font-medium text-gray-500 w-16 flex-shrink-0">Date:</span>
                            <span className="text-sm text-gray-700">{emailHeaderDateTime}</span>
                          </div>
                        )}
                      </div>
                      {document.email_body_html ? (
                        <iframe
                          key={`${document.id}-email-body`}
                          title="Email body"
                          className="min-h-0 w-full flex-1 border-0 bg-white"
                          srcDoc={emailPreviewSrcDoc}
                          sandbox="allow-popups allow-popups-to-escape-sandbox"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="min-h-0 flex-1 overflow-auto p-4 text-sm text-gray-500">
                          No message body is stored for this email.
                        </div>
                      )}
                    </div>
                  )}
                  {previewMode === 'ocr' && document.raw_ocr_text && (
                    <div className="h-full flex flex-col bg-white rounded border border-gray-300 overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50">
                        <span className="text-xs font-medium text-gray-700">
                          {isCsvUpload ? 'CSV data' : 'OCR Text'}
                        </span>
                        <button
                          onClick={handleCopyOcrText}
                          className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title={isCsvUpload ? 'Copy CSV text' : 'Copy all OCR text'}
                        >
                          {ocrCopied ? (
                            <>
                              <CheckIconSolid className="w-4 h-4 text-green-600" />
                              <span className="text-green-600">Copied!</span>
                            </>
                          ) : (
                            <>
                              <ClipboardDocumentIcon className="w-4 h-4" />
                              <span>Copy</span>
                            </>
                          )}
                        </button>
                      </div>
                      {isCsvUpload ? (
                        <CsvPreviewTable text={ocrPreviewText} />
                      ) : (
                        <pre className="flex-1 overflow-auto p-4 text-xs whitespace-pre-wrap font-mono text-gray-800">
                          {highlightData?.amount
                            ? highlightAmountMatches(ocrPreviewText, highlightData.amount)
                            : ocrPreviewText}
                        </pre>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        </div>

        {showPreviewPanel && (
          <button
            type="button"
            onClick={() => setPreviewSplitOpen((o) => !o)}
            className="absolute right-0 top-1/2 z-20 max-md:hidden md:flex md:h-28 md:w-5 md:-translate-y-1/2 md:flex-col md:items-center md:justify-center md:gap-1 rounded-l-md border border-r-0 border-slate-300/80 bg-slate-100/95 text-slate-600 shadow-sm transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800"
            title={previewSplitOpen ? 'Hide preview panel' : 'Show preview panel'}
            aria-expanded={previewSplitOpen}
          >
            <DocumentTextIcon className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
            {previewSplitOpen ? (
              <ChevronRightIcon className="h-4 w-4 shrink-0" aria-hidden />
            ) : (
              <ChevronLeftIcon className="h-4 w-4 shrink-0" aria-hidden />
            )}
          </button>
        )}

      {showStrongEnrichModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-2 sm:p-4"
          onClick={() => !enrichingSku && setShowStrongEnrichModal(false)}
        >
          <div
            className="max-h-[95vh] w-full max-w-md overflow-y-auto rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-gray-200 p-3 sm:p-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Extract for COGS ledger with stronger model
              </h3>
            </div>
            <div className="space-y-2 p-3 text-sm text-gray-600 sm:p-4">
              <p>
                This runs one extraction pass with <strong>{EXTRACTION_MODEL_STRONG}</strong>{' '}
                (EXTRACTION_MODEL_STRONG) for this document only.
              </p>
              <p>
                Estimated cost: <strong>{STRONG_ENRICHMENT_ESTIMATED_COST}</strong>.
              </p>
              <p className="text-gray-500">
                Use only when standard enrichment failed validation. Product linking still happens on
                the Purchasing review page.
              </p>
            </div>
            <div className="flex flex-col-reverse gap-2 border-t border-gray-200 bg-gray-50 p-3 sm:flex-row sm:justify-end sm:p-4">
              <button
                type="button"
                onClick={() => setShowStrongEnrichModal(false)}
                className="btn btn-secondary"
                disabled={enrichingSku}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleStrongSkuEnrichment()}
                className="btn btn-primary"
                disabled={enrichingSku}
              >
                {enrichingSku ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    Running…
                  </>
                ) : (
                  'Run strong extraction'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refresh Categories Confirmation Modal */}
      {showRefreshModal && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-2 sm:p-4"
          onClick={() => !refreshingCategories && setShowRefreshModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[95vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 sm:p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Refresh Categories</h3>
            </div>
            <div className="p-3 sm:p-4">
              <p className="text-sm text-gray-600">
                This will re-infer categories for all line items based on the current vendor name
                ({formData.vendor_name}). Any existing category selections will be replaced if a
                match is found in the history.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Line items without a matching category in the history will remain unchanged.
              </p>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-3 sm:p-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={() => setShowRefreshModal(false)}
                className="btn btn-secondary"
                disabled={refreshingCategories}
              >
                Cancel
              </button>
              <button
                onClick={handleRefreshCategories}
                className="btn btn-primary"
                disabled={refreshingCategories}
              >
                {refreshingCategories ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <ArrowPathIcon className="w-4 h-4" />
                    Refresh Categories
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk category for selected line items */}
      {showBulkCategoryModal && (
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/50 p-2 py-8 sm:p-4 sm:py-10"
          onClick={() => {
            setShowBulkCategoryModal(false)
            setBulkCategoryId(null)
            setBulkCategoryName(null)
            setBulkCategoryError(null)
          }}
        >
          <div
            className="my-auto w-full max-w-md rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-gray-200 p-3 sm:p-4">
              <div className="flex items-center gap-2">
                <TagIcon className="h-5 w-5 shrink-0 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Set category on selected</h3>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Choose a QuickBooks category to apply to {selectedLineRowKeys.size} selected line item
                {selectedLineRowKeys.size === 1 ? '' : 's'}.
              </p>
            </div>
            <div className="space-y-4 p-3 sm:p-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Category</label>
                <SearchableSelect
                  options={categoryOptions}
                  value={bulkCategoryId}
                  onChange={(id, label) => {
                    setBulkCategoryId(id)
                    setBulkCategoryName(label)
                    setBulkCategoryError(null)
                  }}
                  placeholder="Search categories..."
                  disabled={loadingQBO}
                  className="w-full min-w-0"
                />
              </div>
              {bulkCategoryError && (
                <div className="flex items-center gap-2 rounded bg-red-50 p-2 text-sm text-red-600">
                  <XCircleIcon className="h-4 w-4 shrink-0" />
                  <span>{bulkCategoryError}</span>
                </div>
              )}
            </div>
            <div className="flex flex-col-reverse gap-2 border-t border-gray-200 bg-gray-50 p-3 sm:flex-row sm:justify-end sm:p-4">
              <button
                type="button"
                onClick={() => {
                  setShowBulkCategoryModal(false)
                  setBulkCategoryId(null)
                  setBulkCategoryName(null)
                  setBulkCategoryError(null)
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyBulkCategoryToSelection}
                className="btn btn-primary"
                disabled={loadingQBO}
              >
                <CheckIcon className="h-4 w-4" />
                Apply to {selectedLineRowKeys.size}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Line Item Correction Modal */}
      {showCorrectionModal && (
        <div 
          className={`fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 ${correctionModalPosition ? '' : 'bg-black/30'}`}
          onClick={(e) => {
            if (!isCorrectingItems && e.target === e.currentTarget) {
              setShowCorrectionModal(false)
              setCorrectionModalPosition(null)
              setCorrectionInstructions('')
              setCorrectionError(null)
            }
          }}
        >
          <div 
            className="correction-modal-container bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[95vh] overflow-y-auto"
            style={correctionModalPosition ? {
              position: 'fixed',
              left: correctionModalPosition.x,
              top: correctionModalPosition.y,
            } : undefined}
          >
            <div 
              className={`flex items-center gap-2 border-b border-gray-200 p-3 sm:p-4 ${maxMd ? '' : 'cursor-move'}`}
              onMouseDown={maxMd ? undefined : handleCorrectionModalDragStart}
            >
              <Bars3Icon className="w-4 h-4 text-gray-400 flex-shrink-0 pointer-events-none max-md:hidden" />
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 pointer-events-none" />
              <h3 className="flex-1 pointer-events-none text-lg font-semibold text-gray-900">Fix Selected Line Items</h3>
              {!maxMd && (
                <span className="pointer-events-none text-xs text-gray-400">drag to move</span>
              )}
            </div>
            <div className="p-3 sm:p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Describe what's wrong
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {!amazonReportLocked ? (
                    <button
                      type="button"
                      className="btn btn-secondary text-xs py-1.5 px-2.5"
                      disabled={isCorrectingItems}
                      onClick={() =>
                        setCorrectionInstructions(
                          'These line items were not extracted from the PDF correctly. Try again, and update the selected line items with the correct values.',
                        )
                      }
                    >
                      Re-Extract
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-secondary text-xs py-1.5 px-2.5"
                    disabled={isCorrectingItems}
                    onClick={() =>
                      setCorrectionInstructions(
                        "These line items don't match the order of the line items in the PDF. Re-order these so they strictly match the visual order of the line items shown in the PDF.",
                      )
                    }
                  >
                    Re-Order
                  </button>
                </div>
                <textarea
                  className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                  rows={4}
                  placeholder="e.g., 'The total amounts are offset by one row - each amount belongs to the item below it' or 'These items are out of order, please match the order shown in the PDF'"
                  value={correctionInstructions}
                  onChange={(e) => setCorrectionInstructions(e.target.value)}
                  disabled={isCorrectingItems}
                />
              </div>

              {correctionError && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                  <XCircleIcon className="w-4 h-4 flex-shrink-0" />
                  <span>{correctionError}</span>
                </div>
              )}
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-3 sm:p-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowCorrectionModal(false)
                  setCorrectionInstructions('')
                  setCorrectionError(null)
                  setCorrectionModalPosition(null)
                }}
                className="btn btn-secondary"
                disabled={isCorrectingItems}
              >
                Cancel
              </button>
              <button
                onClick={handleCorrectLineItems}
                className="btn btn-primary"
                disabled={isCorrectingItems || !correctionInstructions.trim()}
              >
                {isCorrectingItems ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    Correcting...
                  </>
                ) : (
                  <>
                    <CheckIcon className="w-4 h-4" />
                    Correct Items
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Undo Correction Toast */}
      {previousLineItems && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-3">
          <span className="text-sm">Line items corrected</span>
          <button
            onClick={handleUndoCorrection}
            className="flex items-center gap-1 text-sm text-blue-300 hover:text-blue-200 transition-colors"
          >
            <ArrowUturnLeftIcon className="w-4 h-4" />
            Undo
          </button>
          <button
            onClick={() => setPreviousLineItems(null)}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Approve Confirmation Modal - shown when there are warning flags */}
      {showApproveConfirm && canApproveOrArchive && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-2 sm:p-4"
          onClick={() => setShowApproveConfirm(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[95vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 sm:p-4 border-b border-amber-200 bg-amber-50 flex items-center gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 text-amber-500" />
              <h3 className="text-lg font-semibold text-gray-900">
                {hasNonPayableNotice ? 'Override Non-Payable Notice' : 'Review Warnings'}
              </h3>
            </div>
            <div className="p-3 sm:p-4">
              <p className="text-sm text-gray-600 mb-3">
                {hasNonPayableNotice
                  ? 'This document was flagged with "do not pay" or "not an invoice" language. Are you sure you want to override this and send it to QuickBooks?'
                  : 'This document has warnings. Are you sure you want to approve it?'}
              </p>
              <ul className="space-y-2">
                {warningFlags.filter((f) => f !== 'qbo_duplicate').map((flag, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2.5 text-xs p-2.5 rounded-lg border text-amber-700 bg-amber-50 border-amber-200"
                    >
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                      <span>{formatFlagMessage(flag)}</span>
                    </li>
                  ))}
              </ul>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-3 sm:p-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={() => setShowApproveConfirm(false)}
                className="btn btn-secondary w-full sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmApprove}
                className="btn btn-primary w-full sm:w-auto"
              >
                <CheckIcon className="w-4 h-4" />
                {hasNonPayableNotice ? 'Override and Approve' : 'Approve Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEmailRuleEdit && document.email_rule_id != null && canEdit && (
        <EmailRuleEditModal
          ruleId={document.email_rule_id}
          onClose={() => setShowEmailRuleEdit(false)}
          onSaved={handleEmailRuleSaved}
          subtitle="Changes apply to future documents processed with this rule. Reprocess this document to apply updated PDF hints."
        />
      )}
      </div>
    </div>,
    window.document.body
  )
}
